// StaffDocumentsReviewPage.jsx
// ✅ Uses sub.cardSnapshots as the source-of-truth for requirements + subtitle
// ✅ Attaches uploaded files into each requirement card
// ✅ Still shows "Uncategorized" for files without matching cardSnapshots
// ✅ Falls back to parsing cardId from objectPath if needed
//
// ✅ NEW (Option 2 support):
// - Supports cardSnapshots[cardId].section ("Identification documents" / "Source of Wealth corroboration")
// - Supports cardSnapshots[cardId].options [{key,label}] for approve modal (stable IDs)
// - Stores review.requirementKey + review.requirementText on approval (stable mapping)

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { auth, db, storage } from "../firebase";
import { createReviewFileResolver, reviewFilesForStorage } from "../utils/reviewFileResolver";
import { doc, getDoc, onSnapshot, updateDoc, serverTimestamp, FieldPath } from "firebase/firestore";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import { CircleFlag } from "react-circle-flags";
import PdfDocumentPreview from "../components/ui/PdfDocumentPreview";
import { requiresDocumentReview } from '../utils/documentReviewPolicy';
import countries from "i18n-iso-countries";
import "./css/StaffDocumentsReviewPage.css";
import { useAppLoading } from "../context/AppLoadingContext";
import { useStaffTopBand } from "../components/ui/StaffTopBandContext";
import { useAuth } from "../auth/AuthProvider";
import { canDecideDocumentReview } from "../auth/permissions";
import KycReadOnlyBanner from "../kyc/sections/common/KycReadOnlyBanner";
import iconMailboxD from "../images/mailbox-D.svg";
import iconReturnWhite from "../images/return-white.svg";
import iconThumbsDown from "../images/thumbs-down-B.svg";
import iconThumbsUp from "../images/thumbs-up-B.svg";
import iconUtilityBlueDoc from "../images/utility-blue-doc.svg";
import iconUtilityBlueDocHeader from "../images/utility-blue-doc-b2.svg";
import iconIdCardBlueDoc from "../images/id-card-blue-doc.svg";
import iconIdCardBlueDocHeader from "../images/id-card-blue-doc-2.svg";
import iconSowBlueDoc from "../images/sow-blue.svg";
import iconCorporateDocBlue from "../images/corporate-doc-blue-2.svg";
import iconPassport from "../images/passport.svg";
import iconAddress from "../images/address.svg";
import iconBirthday from "../images/birthday.svg";
import iconCircleBlueDownload from "../images/circle-blue-download.svg";
import iconCircleBlueView from "../images/circle-blue-view.svg";
import iconScannFail from "../images/scann-fail-5.svg";
import iconScannCheck from "../images/scann-check.svg";
import iconScannOk from "../images/scann-ok-2.svg";
import {
  DEFAULT_UBO_DOCUMENT_SCOPE,
  UBO_DOCUMENT_SCOPE_ALL_UBOS,
  UBO_DOCUMENT_SCOPE_THRESHOLD_AND_DIRECT,
  UBO_DOCUMENT_SCOPE_THRESHOLD_ONLY,
  SOW_LABEL_BY_VALUE,
  normalizeUboDocumentScope,
} from "../utils/sowDocumentPolicy";
import {
  getDirectIndividualShareholderRows,
  getIndirectIndividualUboRows,
  getQualifiedIndividualUboRows,
} from "../utils/uboSelection";
import { MYKYC_API_BASE } from "../config/api";

/* -------------------- Helpers -------------------- */

function safeStr(v) {
  return String(v || "").trim();
}

function stripUndefinedForFirestore(value) {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (value instanceof Date) return value;
  if (Array.isArray(value)) {
    return value
      .map((item) => stripUndefinedForFirestore(item))
      .filter((item) => item !== undefined);
  }
  if (typeof value === "object") {
    if (value.constructor && value.constructor !== Object) return value;
    return Object.entries(value).reduce((acc, [key, entry]) => {
      const cleaned = stripUndefinedForFirestore(entry);
      if (cleaned !== undefined) acc[key] = cleaned;
      return acc;
    }, {});
  }
  return value;
}

function sanitizeFirestoreUpdatePayload(payload = {}) {
  return Object.entries(payload).reduce((acc, [key, value]) => {
    const cleaned = stripUndefinedForFirestore(value);
    if (cleaned !== undefined) acc[key] = cleaned;
    return acc;
  }, {});
}

function toTitleCaseLabel(v) {
  return safeStr(v).replace(/\b([a-z])/g, (m) => m.toUpperCase());
}

function sortKycUpdateOptionsAddFirst(options = []) {
  return (Array.isArray(options) ? options : [])
    .map((option, index) => ({ option, index }))
    .sort((a, b) => {
      const aIsAdd = /^add\b/i.test(safeStr(a.option?.label));
      const bIsAdd = /^add\b/i.test(safeStr(b.option?.label));
      if (aIsAdd !== bIsAdd) return aIsAdd ? -1 : 1;
      return a.index - b.index;
    })
    .map(({ option }) => option);
}

function hasSelectedKycUpdateOption(options = [], selected = {}) {
  return (Array.isArray(options) ? options : []).some((option) => selected?.[option?.key] !== false);
}

/* Helpers country flags */
function toIso2Lower(v) {
  const cc = safeStr(v).toUpperCase();
  if (/^[A-Z]{2}$/.test(cc)) return cc.toLowerCase();
  if (/^[A-Z]{3}$/.test(cc)) {
    const iso2 = countries.alpha3ToAlpha2(cc);
    return iso2 ? iso2.toLowerCase() : "";
  }
  return "";
}

// removes stale inline meta such as "(Nationality: —)" from titles
function stripMetaInTitle(title) {
  return String(title || "")
    .replace(/\s*\(Nationality:\s*[^)]*\)\s*/g, " ")
    .replace(/\s*\(Country:\s*[^)]*\)\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function getHeaderFlagsFromMeta(meta) {
  const kind = safeStr(meta?.kind);

  if (kind === "entity") {
    return {
      nationality: "",
      domicile: "",
      incorporation: toIso2Lower(meta?.incorporationIso2),
    };
  }

  if (kind === "individual") {
    const nat = toIso2Lower(meta?.nationalityIso2);
    const dom = toIso2Lower(meta?.domicileIso2);

    // if same country, show only one flag
    const domicile = dom && dom !== nat ? dom : "";

    return {
      nationality: nat,
      domicile,
      incorporation: "",
    };
  }

  return { nationality: "", domicile: "", incorporation: "" };
}

function normPartyType(v) {
  const s = safeStr(v).toLowerCase();
  if (s === "individual" || s === "person" || s === "natural person") {
    return "individual";
  }
  if (s === "trust") return "trust";
  if (s === "entity" || s === "company" || s === "legal person") {
    return "entity";
  }
  return "";
}

function getQualifiedUboRows(ownership) {
  return getQualifiedIndividualUboRows(ownership);
}

function getThresholdUboRows(ownership) {
  return getQualifiedUboRows(ownership);
}

function getScopedDirectShareholderRows(ownership, uboDocumentScope) {
  if (uboDocumentScope === UBO_DOCUMENT_SCOPE_THRESHOLD_ONLY) {
    return [];
  }

  if (
    uboDocumentScope === UBO_DOCUMENT_SCOPE_THRESHOLD_AND_DIRECT ||
    uboDocumentScope === UBO_DOCUMENT_SCOPE_ALL_UBOS
  ) {
    return getDirectIndividualShareholderRows(ownership);
  }

  return [];
}

function getScopedUboRows(ownership, uboDocumentScope) {
  if (uboDocumentScope === UBO_DOCUMENT_SCOPE_ALL_UBOS) {
    return getIndirectIndividualUboRows(ownership);
  }

  return getThresholdUboRows(ownership);
}

function buildLivePartiesFromKyc(kyc) {
  const customer = kyc?.customer || {};
  const ownership = kyc?.ownership || {};
  const uboDocumentScope = normalizeUboDocumentScope(
    kyc?.documentsChecklist?.uboDocumentScope || DEFAULT_UBO_DOCUMENT_SCOPE
  );
  const parties = [];
  const legalArrangements = Array.isArray(
    ownership?.legalArrangements?.arrangements
  )
    ? ownership.legalArrangements.arrangements
    : [];
  const customerIsTrust = !!ownership?.uboFlags?.accountHolderTrust;

  const customerType = customerIsTrust
    ? "trust"
    : normPartyType(customer?.type) || "individual";
  const customerName = safeStr(customer?.name) || "Customer";

  parties.push({
    partyId: "customer",
    name: customerName,
    role:
      customerType === "entity"
        ? "Customer (Entity)"
        : customerType === "trust"
        ? "Customer (Trust)"
        : "Customer (Individual)",
    partyType: customerType,
    dateOfBirth:
      customer?.dobOrIncorporationDate ||
      customer?.dateOfBirth ||
      customer?.dob ||
      "",
    idCountry: customer?.nationalityOrIncorporationCountry || "",
    addressCountry: customer?.country || "",
    corporateCountry: customer?.nationalityOrIncorporationCountry || "",
  });

  const directDocumentNodeIds = new Set();

  getScopedDirectShareholderRows(ownership, uboDocumentScope).forEach((r) => {
    const name = safeStr(r?.name);
    if (!name) return;
    const holderType = normPartyType(
      r?.holderType || r?.shareholderEntityType || r?.partyType || r?.type
    );
    if (!holderType || holderType === "trust") return;

    const treeNodeId = safeStr(r?.treeNodeId);
    if (treeNodeId) directDocumentNodeIds.add(treeNodeId);

    parties.push({
      partyId: `shareholder:${r?._rowId || r?._docRef || name}`,
      name,
      role: "Direct shareholder",
      partyType: holderType,
      dateOfBirth:
        r?.dobOrIncorporationDate ||
        r?.dateOfBirth ||
        r?.dob ||
        r?.birthDate ||
        "",
      idCountry: r?.nationality || "",
      addressCountry: r?.countryOfResidence || "",
      corporateCountry: r?.countryOfResidence || "",
    });
  });

  getScopedUboRows(ownership, uboDocumentScope).forEach((r) => {
      const name = safeStr(r?.name);
      if (!name) return;
      const treeNodeId = safeStr(r?.treeNodeId || r?.subjectNodeId);
      if (treeNodeId && directDocumentNodeIds.has(treeNodeId)) return;
      parties.push({
        partyId: `ubo:${r?._rowId || r?._docRef || name}`,
        name,
        role: "UBO (Ultimate beneficial owner)",
        partyType: "individual",
        dateOfBirth:
          r?.dobOrIncorporationDate ||
          r?.dateOfBirth ||
          r?.dob ||
          r?.birthDate ||
          "",
        idCountry: r?.nationality || "",
        addressCountry: r?.countryOfResidence || "",
        corporateCountry: "",
      });
    });

  (ownership?.signatories || ownership?.controllers || []).forEach((r) => {
    const name = safeStr(r?.name);
    if (!name) return;
    const cap = safeStr(r?.capacity);
      parties.push({
        partyId: `signatory:${r?._rowId || r?._docRef || name}`,
        name,
        role: cap ? `Signatory (${cap})` : "Signatory",
        partyType: "individual",
        dateOfBirth:
          r?.dobOrIncorporationDate ||
          r?.dateOfBirth ||
          r?.dob ||
          r?.birthDate ||
          "",
        idCountry: r?.nationality || "",
        addressCountry: r?.countryOfResidence || "",
        corporateCountry: "",
    });
  });

  legalArrangements.forEach((arr, idx) => {
    const arrangementType = safeStr(arr?.arrangementType).toLowerCase();
    if (arrangementType !== "trust") return;

    const isAccountHolderTrustArrangement =
      customerIsTrust && safeStr(arr?.source) === "account_holder";

    const trustName =
      safeStr(arr?.name) ||
      safeStr(arr?.relatedShareholderName) ||
      `Trust ${idx + 1}`;

    if (!isAccountHolderTrustArrangement) {
      parties.push({
        partyId: `trust:${arr?._rowId || idx}`,
        name: trustName,
        role: "Trust",
        partyType: "trust",
        idCountry: "",
        addressCountry: "",
        corporateCountry: "",
      });
    }

    const trustee = (Array.isArray(arr?.parties) ? arr.parties : []).find(
      (p) => safeStr(p?.role) === "Trustee"
    );
    const trusteeName = safeStr(trustee?.name);
    if (!trusteeName) return;

    const trusteeType =
      normPartyType(trustee?.trusteeKind) === "entity" ? "entity" : "individual";

    parties.push({
      partyId: `trustee:${arr?._rowId || idx}:${trustee?._rowId || trusteeName}`,
      name: trusteeName,
      role: `Trustee of ${trustName}`,
      partyType: trusteeType,
      dateOfBirth:
        trustee?.dobOrIncorporationDate ||
        trustee?.dateOfBirth ||
        trustee?.dob ||
        trustee?.birthDate ||
        "",
      idCountry: trustee?.nationality || "",
      addressCountry: trustee?.domicile || "",
      corporateCountry: trustee?.domicile || "",
    });
  });

  return parties;
}

function partyToReviewMeta(p) {
  if (!p) return null;
  const partyType = normPartyType(p?.partyType);
  return {
    kind: partyType,
    partyId: safeStr(p?.partyId),
    partyName: safeStr(p?.name),
    partyRole: safeStr(p?.role),
    registeredAddress: safeStr(
      p?.registeredAddress ||
        p?.registered_address ||
        p?.partyAddress ||
        p?.address ||
        p?.addressLine ||
        p?.fullAddress
    ),
    dateOfBirth:
      p?.dateOfBirth ||
      p?.dob ||
      p?.dobOrIncorporationDate ||
      p?.birthDate ||
      "",
    nationalityIso2: safeStr(p?.idCountry),
    domicileIso2: safeStr(p?.addressCountry),
    incorporationIso2: safeStr(p?.corporateCountry || p?.addressCountry),
  };
}

function getPartyIdFromRequirementKey(key) {
  const parsed = parseRequirementKey(key);
  return parsed?.kind === "party" ? parsed.partyId : "";
}

function updateDocFieldPath(...segments) {
  return new FieldPath(...segments.map((segment) => safeStr(segment)));
}

function parseRequirementKey(requirementKey) {
  const key = safeStr(requirementKey);
  if (!key) return null;

  if (key.includes("|")) {
    const parts = key.split("|").map(safeStr);
    const kind = parts[0];
    if (kind === "party" && parts.length >= 3) {
      return {
        kind,
        partyId: parts.slice(1, -1).join("|"),
        docKey: parts[parts.length - 1],
      };
    }
    if (kind === "sow" && parts.length >= 3) {
      return {
        kind,
        sowKey: parts[1],
        docKey: parts[parts.length - 1],
      };
    }
    return null;
  }

  const parts = key.split(":").map(safeStr);
  const kind = parts[0];

  if (kind === "party" && parts.length >= 3) {
    return {
      kind,
      partyId: parts.slice(1, -1).join(":"),
      docKey: parts[parts.length - 1],
    };
  }

  if (kind === "sow" && parts.length >= 3) {
    const [, sowKey, maybeMode, ...rest] = parts;
    const hasMode = maybeMode === "req" || maybeMode === "opt" || maybeMode === "grp";
    const groupId = maybeMode === "grp" ? safeStr(rest[0]) : "";
    return {
      kind,
      sowKey,
      docKey: maybeMode === "grp"
        ? rest.slice(1).join(":")
        : hasMode
        ? rest.join(":")
        : parts.slice(2).join(":"),
      mode: hasMode ? maybeMode : "",
      groupId,
    };
  }

  return null;
}

function getPartyIdFromSnap(snap) {
  const metaPartyId = safeStr(snap?.meta?.partyId);
  if (metaPartyId) return metaPartyId;
  const option = (Array.isArray(snap?.options) ? snap.options : []).find((o) =>
    safeStr(o?.key).startsWith("party:")
  );
  return getPartyIdFromRequirementKey(option?.key);
}

function mergeSnapshotMetaWithLive(snapshotMeta, liveMeta) {
  if (!liveMeta) return snapshotMeta || null;
  const base = snapshotMeta || {};
  return {
    ...base,
    kind: safeStr(liveMeta.kind) || safeStr(base.kind),
    partyId: safeStr(liveMeta.partyId) || safeStr(base.partyId),
    partyName: safeStr(liveMeta.partyName) || safeStr(base.partyName),
    partyRole: safeStr(liveMeta.partyRole) || safeStr(base.partyRole),
    registeredAddress:
      safeStr(liveMeta.registeredAddress) ||
      safeStr(liveMeta.partyAddress) ||
      safeStr(base.registeredAddress) ||
      safeStr(base.partyAddress),
    dateOfBirth:
      liveMeta.dateOfBirth ||
      liveMeta.dob ||
      liveMeta.dobOrIncorporationDate ||
      "",
    nationalityIso2:
      safeStr(liveMeta.nationalityIso2),
    domicileIso2: safeStr(liveMeta.domicileIso2),
    incorporationIso2:
      safeStr(liveMeta.incorporationIso2),
  };
}

/*Helper approve requirement*/

async function applyRequirementToKycChecklist({ clientId, requirementKey, idExpiryDate = "" }) {
  const key = safeStr(requirementKey);
  if (!clientId || !key) return;

  const parsed = parseRequirementKey(key);

  if (parsed?.kind === "party" && parsed.partyId && parsed.docKey) {
    const kycRef = doc(db, "kyc", clientId);
    const cleanExpiry = parsed.docKey === "id" ? normalizeDocCheckDate(idExpiryDate) : "";

    const updates = [
      updateDocFieldPath(
        "documentsChecklist",
        "parties",
        parsed.partyId,
        "docs",
        parsed.docKey,
        "obtained"
      ),
      true,
    ];
    if (cleanExpiry) {
      updates.push(
        updateDocFieldPath(
          "documentsChecklist",
          "parties",
          parsed.partyId,
          "docs",
          parsed.docKey,
          "expiryDate"
        ),
        cleanExpiry
      );
    }
    updates.push("updatedAt", serverTimestamp());

    await updateDoc(kycRef, ...updates);
    return;
  }

  if (parsed?.kind === "sow" && parsed.sowKey && parsed.docKey) {
    const kycRef = doc(db, "kyc", clientId);

    await updateDoc(
      kycRef,
      updateDocFieldPath(
        "documentsChecklist",
        "sow",
        parsed.sowKey,
        "docs",
        parsed.docKey,
        "obtained"
      ),
      true,
      "updatedAt",
      serverTimestamp()
    );
    return;
  }

  return;
}

/*Helper reject requirement*/

async function uncheckRequirementInKycChecklist({ clientId, requirementKey }) {
  const key = safeStr(requirementKey);
  if (!clientId || !key) return;

  const parsed = parseRequirementKey(key);

  if (parsed?.kind === "party" && parsed.partyId && parsed.docKey) {
    const kycRef = doc(db, "kyc", clientId);

    const updates = [
      updateDocFieldPath(
        "documentsChecklist",
        "parties",
        parsed.partyId,
        "docs",
        parsed.docKey,
        "obtained"
      ),
      false,
    ];
    if (parsed.docKey === "id") {
      updates.push(
        updateDocFieldPath(
          "documentsChecklist",
          "parties",
          parsed.partyId,
          "docs",
          parsed.docKey,
          "expiryDate"
        ),
        ""
      );
    }
    updates.push("updatedAt", serverTimestamp());

    await updateDoc(kycRef, ...updates);
    return;
  }

  if (parsed?.kind === "sow" && parsed.sowKey && parsed.docKey) {
    const kycRef = doc(db, "kyc", clientId);

    await updateDoc(
      kycRef,
      updateDocFieldPath(
        "documentsChecklist",
        "sow",
        parsed.sowKey,
        "docs",
        parsed.docKey,
        "obtained"
      ),
      false,
      "updatedAt",
      serverTimestamp()
    );
    return;
  }

  return;
}

/* Helpers show badge approved after approval */

function shortUid(uid) {
  const s = safeStr(uid);
  if (!s) return "—";
  return s.length <= 10 ? s : `${s.slice(0, 6)}…${s.slice(-4)}`;
}

function formatReviewedAt(iso) {
  const s = safeStr(iso);
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  // local readable
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function reviewLabelFromFile(f) {
  const key = safeStr(f?.review?.requirementKey);
  const txt = safeStr(f?.review?.requirementText);
  // show friendly text first; fallback to key
  return txt || key || "";
}

function dataConsistencyStatusLabel(status) {
  const s = safeStr(status);
  if (s === "clear") return "Clear";
  if (s === "critical_mismatch") return "Critical mismatch";
  if (s === "manual_review") return "Manual review";
  if (s === "pending") return "Pending";
  return s || "Not run";
}

function dataConsistencyStatusClass(status) {
  const s = safeStr(status);
  if (s === "clear") return "isClear";
  if (s === "critical_mismatch") return "isCritical";
  if (s === "manual_review") return "isManual";
  if (s === "pending") return "isPending";
  return "";
}

function proofOfAddressStatusClass(status) {
  const s = safeStr(status);
  if (s === "passed" || s === "pass" || s === "clear") return "isClear";
  if (s === "failed" || s === "fail") return "isCritical";
  if (s === "pending") return "isPending";
  if (s === "warning" || s === "manual_review") return "isManual";
  return "";
}

function badgeDisplayOrderFromClass(className) {
  const s = safeStr(className);
  if (s.includes("isCritical")) return 0;
  if (s.includes("isExpired") || s.includes("isManual")) return 1;
  if (s.includes("isClear")) return 2;
  if (s.includes("isPending")) return 3;
  return 4;
}

function sortBadgeItems(items, classForItem) {
  return (Array.isArray(items) ? items : [])
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const aOrder = badgeDisplayOrderFromClass(classForItem(a.item));
      const bOrder = badgeDisplayOrderFromClass(classForItem(b.item));
      return aOrder === bOrder ? a.index - b.index : aOrder - bOrder;
    })
    .map(({ item }) => item);
}

function proofOfAddressTitle(result) {
  const s = safeStr(result?.overallStatus || result?.status);
  if (s === "passed") return "Utility document data consistent";
  if (s === "warning") return "Proof of address needs review";
  if (s === "failed") return "Proof of address failed";
  if (s === "pending") return "Checking proof of address";
  return "Proof of address review";
}

function proofOfAddressReasonForCheck(check) {
  const type = safeStr(check?.type);
  const status = safeStr(check?.status);
  if (status === "pass" || status === "match" || status === "skipped") return "";
  if (type === "document_type") return "";
  if (type === "issue_date") {
    const msg = safeStr(check?.message);
    if (/missing/i.test(msg)) return "Issue date missing";
    if (/future/i.test(msg)) return "Issue date invalid";
    if (/older/i.test(msg)) return "Document too old";
    return "Issue date issue";
  }
  if (type === "name_match") return status === "warning" ? "Name to review" : "Name differs";
  if (type === "country_match" || type === "address_country_match" || type === "issuer_country_match") return "Domicile differs";
  if (type === "issuer_check") return "Issuer not recognized";
  if (type === "extraction_confidence") return "Low confidence";
  return safeStr(check?.label) || "Proof of address review";
}

function isProofOfAddressDomicileCheck(check) {
  const type = safeStr(check?.type);
  return type === "country_match" || type === "address_country_match" || type === "issuer_country_match";
}

function proofOfAddressReasonPriority(reason) {
  const check = reason?.check || {};
  const type = safeStr(check?.type || reason?.type);
  const label = safeStr(reason?.label);
  const status = safeStr(reason?.status || check?.status);
  if (type === "document_type") return 10;
  if (type === "name_match" && status !== "warning") return 20;
  if (isProofOfAddressDomicileCheck(check) || reason?.key === "domicile_differs") return 30;
  if (type === "issuer_check") return 40;
  if (type === "issue_date" && label !== "Document too old") return 50;
  if (type === "issue_date" && label === "Document too old") return 60;
  if (type === "name_match") return 70;
  if (type === "extraction_confidence") return 80;
  return 90;
}

function proofOfAddressReasonBadgeClass(reason, fallbackStatus) {
  const check = reason?.check || {};
  const type = safeStr(check?.type || reason?.type);
  const label = safeStr(reason?.label);
  if (type === "issue_date" && label === "Document too old") return "isExpired";
  return proofOfAddressStatusClass(reason?.status || fallbackStatus);
}

function proofOfAddressReasons(result) {
  const checks = Array.isArray(result?.checks) ? result.checks : [];
  const hasFailedDocumentTypeCheck = checks.some((check) => {
    if (safeStr(check?.type) !== "document_type") return false;
    const status = safeStr(check?.status);
    return !["pass", "match", "passed", "clear", "skipped"].includes(status);
  });
  const rawReasons = checks
    .map((check) => ({
      key: `${safeStr(check?.type)}:${safeStr(check?.status)}:${safeStr(check?.message)}`,
      label: proofOfAddressReasonForCheck(check),
      status: safeStr(check?.status),
      check,
    }))
    .filter((reason) => reason.label);
  if (hasFailedDocumentTypeCheck) return [];
  const domicileReasons = rawReasons.filter((reason) => isProofOfAddressDomicileCheck(reason.check));
  const reasons = rawReasons.filter((reason) => !isProofOfAddressDomicileCheck(reason.check));
  if (domicileReasons.length) {
    const status = domicileReasons.some((reason) => safeStr(reason.status) === "failed" || safeStr(reason.status) === "fail")
      ? "failed"
      : domicileReasons[0]?.status || "warning";
    reasons.push({
      key: "domicile_differs",
      label: "Domicile differs",
      status,
      check: domicileReasons[0]?.check,
      checks: domicileReasons.map((reason) => reason.check).filter(Boolean),
    });
  }
  if (reasons.length) {
    return reasons.sort((a, b) => proofOfAddressReasonPriority(a) - proofOfAddressReasonPriority(b));
  }
  const status = safeStr(result?.overallStatus || result?.status);
  if (status === "passed") {
    return [{ key: "passed", label: "Proof of address data consistent", status: "passed" }];
  }
  if (status === "pending") {
    return [{ key: "pending", label: "Proof check pending", status: "pending" }];
  }
  return [{ key: "review", label: "Proof of address review", status: status || "manual_review" }];
}

function utilityIssuerTypeLabel(value) {
  const clean = safeStr(value).replace(/_/g, " ").toLowerCase();
  if (!clean) return "";
  return clean.replace(/\b\w/g, (char) => char.toUpperCase());
}

function proofOfAddressDetailForReason(result, reason) {
  const check = reason?.check || null;
  const type = safeStr(check?.type || reason?.type);
  const reasonChecks = Array.isArray(reason?.checks) ? reason.checks : [];
  const isPassedReason = safeStr(reason?.status || result?.overallStatus || result?.status) === "passed";
  const extracted = result?.extracted || {};
  const expected = result?.expectedParty || {};
  const rows = [];
  const issueDate = safeStr(extracted.issue_date || check?.extractedValue);
  const ageDays = Number(check?.ageDays);
  const rawMaxAgeDays = check?.maxAgeDays;
  const maxAgeDays = rawMaxAgeDays === null ? null : Number(rawMaxAgeDays);
  const ageMonths = Number.isFinite(ageDays) ? Math.max(1, Math.round(ageDays / 30)) : null;
  const maxMonths = Number.isFinite(maxAgeDays) && maxAgeDays > 0 ? Math.max(1, Math.round(maxAgeDays / 30)) : null;
  const issuerCheck = (Array.isArray(result?.checks) ? result.checks : []).find(
    (item) => safeStr(item?.type) === "issuer_check"
  );
  const issuerMatch = issuerCheck?.issuerMatch || check?.issuerMatch || {};
  const issuerTypes = Array.isArray(issuerMatch?.issuerTypes) ? issuerMatch.issuerTypes : [];
  const issuerCountryName = issuerMatch?.issuerCountryIso2
    ? countries.getName(safeStr(issuerMatch.issuerCountryIso2).toUpperCase(), "en")
    : "";

  if (type === "issue_date") {
    const suffix =
      ageMonths && maxMonths && ageDays > maxAgeDays
        ? ` (> ${maxMonths} months)`
        : "";
    rows.push({
      label: "Date of issuance",
      value: `${formatReviewDob(issueDate) || issueDate || "Not available"}${suffix}`,
    });
  } else if (type === "name_match") {
    rows.push({
      label: "Document",
      value: formatIdFullName(check?.extractedValue || extracted.client_name) || "Not available",
    });
    rows.push({
      label: "KYC profile",
      value: safeStr(check?.expectedValue || expected.partyName) || "Not available",
    });
  } else if (isProofOfAddressDomicileCheck(check) || reasonChecks.some(isProofOfAddressDomicileCheck)) {
    const domicileChecks = (reasonChecks.length ? reasonChecks : [check]).filter(isProofOfAddressDomicileCheck);
    const addressCheck = domicileChecks.find((item) => {
      const itemType = safeStr(item?.type);
      return itemType === "country_match" || itemType === "address_country_match";
    });
    const issuerCountryCheck = domicileChecks.find((item) => safeStr(item?.type) === "issuer_country_match");
    const documentDomicileRows = [];
    if (addressCheck) {
      documentDomicileRows.push({
        label: "Document domicile",
        value: formatDocCheckCountry(addressCheck?.extractedValue || extracted.country_iso2 || extracted.country),
      });
    }
    if (issuerCountryCheck) {
      documentDomicileRows.push({
        label: "Issuer country",
        value: formatDocCheckCountry(issuerCountryCheck?.extractedValue),
      });
    }
    if (documentDomicileRows.length > 1) {
      rows.push({
        key: "document_domicile_sources",
        rows: documentDomicileRows,
      });
    } else {
      rows.push(...documentDomicileRows);
    }
    rows.push({
      label: "KYC Domicile",
      value: formatDocCheckCountry(
        addressCheck?.expectedValue || issuerCountryCheck?.expectedValue || check?.expectedValue || expected.domicileIso2 || expected.incorporationIso2
      ),
    });
  } else if (type === "issuer_check") {
    rows.push({
      label: "Issuer",
      value: safeStr(check?.extractedValue || extracted.issuer_name) || "Not available",
    });
    rows.push({
      label: "Status",
      value: "Issuer not recognized. To be verified.",
    });
  } else if (type === "document_type") {
    rows.push({
      label: "Document type",
      value: safeStr(check?.extractedValue || extracted.document_type) || "Not available",
    });
  } else if (type === "extraction_confidence") {
    rows.push({
      label: "Confidence",
      value: formatConfidence(check?.extractedValue || result?.extractionConfidence),
    });
  } else if (isPassedReason) {
    // Success details are carried by the utility document details section below.
  }

  const detailRows = [
    { label: "Issuer", value: safeStr(issuerMatch?.issuerName) || "Not available" },
    {
      label: "Issuer type",
      value: issuerTypes.length
        ? issuerTypes.map(utilityIssuerTypeLabel).filter(Boolean).join(", ")
        : "Not available",
    },
    { label: "Issuer country", value: issuerCountryName || "Not available" },
    { label: "Date of issuance", value: formatReviewDob(issueDate) || issueDate || "Not available" },
  ];
  return {
    title: safeStr(reason?.label) || proofOfAddressTitle(result),
    documentTitle: "Utility Document",
    documentKind: "utility",
    reasonTitle: safeStr(reason?.label) || proofOfAddressTitle(result),
    reasonEyebrow: isPassedReason ? "Check passed" : "Issue",
    status: safeStr(reason?.status || result?.overallStatus || result?.status),
    rows: rows.length || isPassedReason ? rows : [{ label: "Status", value: safeStr(check?.message || result?.messages?.[0]) || "Manual review required" }],
    sections: [
      {
        title: "Utility document details",
        variant: "utilityDocumentDetails",
        rows: detailRows,
      },
    ],
  };
}

function corporateDocumentTitle(result) {
  const s = safeStr(result?.overallStatus || result?.status);
  if (s === "passed") return "Corporate document data consistent";
  if (s === "warning") return "Corporate document needs review";
  if (s === "failed") return "Corporate document data issue";
  if (s === "pending") return "Checking corporate document";
  return "Corporate document";
}

function corporateDocumentReasonForCheck(check) {
  const type = safeStr(check?.type);
  const status = safeStr(check?.status);
  if (status === "pass" || status === "match" || status === "skipped") return "";
  if (type === "corporate_company_name_match") return "Company name differs";
  if (type === "corporate_country_match") return "Country differs";
  if (type === "corporate_incorporation_date_match") return "Incorporation date differs";
  if (type === "corporate_registered_address_match") return "Registered address differs";
  if (type === "corporate_document_type") return "";
  if (type === "corporate_issue_date") {
    if (!safeStr(check?.extractedValue)) return "No issue date identified";
    return "Document too old";
  }
  if (type === "corporate_entity_status") return "Entity status needs review";
  return safeStr(check?.label) || "Corporate document review";
}

function corporateDocumentReasonPriority(reason) {
  const type = safeStr(reason?.check?.type || reason?.type);
  if (type === "corporate_company_name_match") return 10;
  if (type === "corporate_country_match") return 20;
  if (type === "corporate_incorporation_date_match") return 30;
  if (type === "corporate_document_type") return 40;
  if (type === "corporate_issue_date") return 50;
  if (type === "corporate_entity_status") return 60;
  if (type === "corporate_registered_address_match") return 80;
  return 90;
}

function corporateDocumentReasonBadgeClass(reason, fallbackStatus) {
  const type = safeStr(reason?.check?.type || reason?.type);
  if (type === "corporate_company_name_match" || type === "corporate_country_match") return "isCritical";
  if (
    type === "corporate_incorporation_date_match" ||
    type === "corporate_registered_address_match" ||
    type === "corporate_document_type" ||
    type === "corporate_issue_date" ||
    type === "corporate_entity_status"
  ) {
    return "isManual";
  }
  const status = safeStr(reason?.status || fallbackStatus);
  if (status === "passed" || status === "pass") return "isClear";
  if (status === "failed" || status === "fail") return "isCritical";
  if (status === "warning" || status === "manual_review") return "isManual";
  if (status === "pending") return "isPending";
  return "";
}

function corporateDocumentReasons(result) {
  if (!result || safeStr(result?.assessmentStatus) !== "assessed") return [];
  const checks = Array.isArray(result?.checks) ? result.checks : [];
  const hasFailedDocumentTypeCheck = checks.some((check) => {
    if (safeStr(check?.type) !== "corporate_document_type") return false;
    const status = safeStr(check?.status);
    return !["pass", "match", "passed", "clear", "skipped"].includes(status);
  });
  const reasons = checks
    .map((check) => ({
      key: `${safeStr(check?.type)}:${safeStr(check?.status)}:${safeStr(check?.message)}`,
      label: corporateDocumentReasonForCheck(check),
      status: safeStr(check?.status),
      check,
    }))
    .filter((reason) => reason.label);
  if (hasFailedDocumentTypeCheck) return [];
  if (reasons.length) {
    return reasons.sort((a, b) => corporateDocumentReasonPriority(a) - corporateDocumentReasonPriority(b));
  }
  const status = safeStr(result?.overallStatus || result?.status);
  if (status === "passed") {
    return [{ key: "passed", label: "Corporate document data consistent", status: "passed" }];
  }
  return [];
}

function corporateDocumentDisplayType(extracted = {}, check = null) {
  const matchedLabel = safeStr(
    check?.matchedExpectedDocumentLabel ||
      check?.matched_expected_document_label ||
      extracted?.matched_expected_document_label
  );
  const matchConfidence = Math.max(0, Math.min(1, Number(check?.matchConfidence ?? extracted?.match_confidence ?? 0)));
  const isMatched =
    extracted?.is_one_of_expected_document_types === true &&
    !!matchedLabel &&
    matchConfidence >= 0.75;
  if (isMatched) return matchedLabel;
  return (
    safeStr(
      check?.identifiedDocumentType ||
        check?.identified_document_type ||
        extracted?.semantic_document_type ||
        extracted?.identified_document_type
    ) ||
    safeStr(extracted?.visible_document_title) ||
    "Not available"
  );
}

function corporateDocumentDetailForReason(result, reason) {
  const check = reason?.check || null;
  const type = safeStr(check?.type || reason?.type);
  const extracted = result?.extracted || {};
  const expected = result?.expectedParty || {};
  const expectedLabels = Array.isArray(result?.expectedDocumentLabels)
    ? result.expectedDocumentLabels
    : [];
  const rows = [];

  if (type === "corporate_company_name_match") {
    rows.push({ label: "Document", value: formatCorporateCompanyName(check?.extractedValue || extracted.company_name) || "Not available" });
    rows.push({ label: "KYC profile", value: safeStr(check?.expectedValue || expected.partyName) || "Not available" });
  } else if (type === "corporate_country_match") {
    rows.push({ label: "Document country", value: formatDocCheckCountry(check?.extractedValue || extracted.jurisdiction_country_iso2 || extracted.issuer_country_iso2) });
    rows.push({ label: "KYC incorporation country", value: formatDocCheckCountry(check?.expectedValue || expected.incorporationIso2) });
  } else if (type === "corporate_incorporation_date_match") {
    rows.push({ label: "Document", value: formatReviewDob(check?.extractedValue || extracted.incorporation_date) || safeStr(check?.extractedValue || extracted.incorporation_date) || "Not available" });
    rows.push({ label: "KYC profile", value: formatReviewDob(check?.expectedValue || expected.incorporationDate) || safeStr(check?.expectedValue || expected.incorporationDate) || "Not available" });
  } else if (type === "corporate_registered_address_match") {
    rows.push({ label: "Document", value: safeStr(check?.extractedValue || extracted.registered_address) || "Not available" });
    rows.push({ label: "KYC profile", value: safeStr(check?.expectedValue || expected.registeredAddress) || "Not available" });
  } else if (type === "corporate_document_type") {
    rows.push({ label: "Document type", value: corporateDocumentDisplayType(extracted, check) });
    rows.push({ label: "Expected", value: safeStr(check?.expectedValue) || expectedLabels.join(", ") || "Not available" });
  } else if (type === "corporate_issue_date") {
    rows.push({ label: "Issue date", value: formatReviewDob(check?.extractedValue || extracted.issue_date) || safeStr(check?.extractedValue || extracted.issue_date) || "Not available" });
  } else if (type === "corporate_entity_status") {
    rows.push({ label: "Entity status", value: safeStr(check?.extractedValue || extracted.entity_status) || "Not available" });
  } else if (safeStr(reason?.status || result?.overallStatus || result?.status) === "passed") {
    // Success details are carried by the extracted-data section below.
  } else {
    rows.push({ label: "Status", value: safeStr(check?.message || result?.messages?.[0]) || "Corporate document data is consistent." });
  }

  const detailRows = [
    { label: "Document type", value: corporateDocumentDisplayType(extracted) },
    { label: "Company name", value: formatCorporateCompanyName(extracted.company_name) || "Not available" },
    { label: "Incorporation date", value: formatReviewDob(extracted.incorporation_date) || safeStr(extracted.incorporation_date) || "Not available" },
    { label: "Registered address", value: safeStr(extracted.registered_address) || "Not available" },
    { label: "Issue date", value: formatReviewDob(extracted.issue_date) || safeStr(extracted.issue_date) || "Not available" },
    { label: "Issuer", value: safeStr(extracted.issuer_name) || "Not available" },
    { label: "Issuer country", value: formatDocCheckCountry(extracted.issuer_country_iso2 || extracted.issuer_country) },
    { label: "Entity status", value: safeStr(extracted.entity_status) || "Not available" },
    { label: "Legal form", value: safeStr(extracted.legal_form) || "Not available" },
  ];

  return {
    title: safeStr(reason?.label) || corporateDocumentTitle(result),
    documentTitle: "Corporate Document",
    documentKind: "corporate",
    reasonTitle: safeStr(reason?.label) || corporateDocumentTitle(result),
    reasonEyebrow: safeStr(reason?.status || result?.overallStatus || result?.status) === "passed" ? "Check passed" : "Issue",
    status: safeStr(reason?.status || result?.overallStatus || result?.status),
    rows,
    sections: [
      {
        title: "Corporate document extracted",
        variant: "corporateDocumentDetails",
        rows: detailRows,
      },
    ],
  };
}

function dataConsistencyTitle(result) {
  const s = safeStr(result?.overallStatus || result?.status);
  if (s === "clear") return "ID data checked";
  if (s === "pending") return "Checking ID data";
  if (s === "no_id_data_detected") return "No ID data detected";
  if (s === "failed") return "Manual review required";
  if (s === "manual_review" || s === "critical_mismatch") return "Manual review required";
  return "Not scanned";
}

function dataConsistencyShortReason(result, fallback = "") {
  const shortFromChecks = dataConsistencyShortReasonsFromChecks(result);
  if (shortFromChecks.length) return shortFromChecks[0]?.label || "";

  const message = safeStr(result?.messages?.[0] || fallback);
  if (!message) return "";
  const cleaned = message
    .replace(/^OK\s*-\s*/i, "")
    .replace(/^Review\s*-\s*/i, "")
    .replace(/^Critical\s*-\s*/i, "")
    .replace(/\s*Manual review required\.?$/i, "")
    .replace(/\s*Please review (the document )?manually\.?$/i, "")
    .replace(/[.。]+$/g, "")
    .trim();
  if (/^this file is being checked automatically$/i.test(cleaned)) {
    return "ID check pending";
  }
  if (/no readable .*mrz.*detected/i.test(cleaned)) {
    return "ID data could not be read";
  }
  return cleaned;
}

function dataConsistencyShortReasonForCheck(check) {
  const type = safeStr(check?.type);
  const status = safeStr(check?.status);
  const extracted = safeStr(check?.extractedValue);
  const profile = safeStr(check?.profileValue);

  if (type === "name") {
    if (!extracted) return "Name not read";
    if (!profile) return "Name missing in KYC";
    return "Name differs";
  }

  if (type === "dateOfBirth") {
    if (!extracted) return "DOB not read";
    if (!profile) return "DOB missing in KYC";
    return "DOB differs";
  }

  if (type === "nationality") {
    if (!extracted) return "Nationality not read";
    if (!profile) return "Nationality missing in KYC";
    return "Nationality differs";
  }

  if (type === "mrzChecksum") {
    return /partially verified|identity fields are verified/i.test(safeStr(check?.message))
      ? "MRZ partially verified"
      : "MRZ checksum failed";
  }
  if (type === "ocrConfidence") return "Low OCR confidence";
  if (status === "critical_mismatch") return "ID data differs";
  if (status === "manual_review") return "Manual review needed";
  return "";
}

function dataConsistencyShortReasonsFromChecks(result) {
  const checks = Array.isArray(result?.checks) ? result.checks : [];
  const issueChecks = checks.filter((check) => {
    const status = safeStr(check?.status);
    return status && status !== "match" && status !== "expired";
  });
  const priorityOrder = ["dateOfBirth", "nationality", "name", "mrzChecksum", "ocrConfidence"];
  const statusPriority = {
    critical_mismatch: 0,
    manual_review: 1,
    pending: 2,
    failed: 3,
  };
  const orderedChecks = [...issueChecks].sort((a, b) => {
    const aStatus = safeStr(a?.status);
    const bStatus = safeStr(b?.status);
    const aStatusRank = statusPriority[aStatus] ?? 9;
    const bStatusRank = statusPriority[bStatus] ?? 9;
    if (aStatusRank !== bStatusRank) return aStatusRank - bStatusRank;

    const aTypeRank = priorityOrder.indexOf(safeStr(a?.type));
    const bTypeRank = priorityOrder.indexOf(safeStr(b?.type));
    return (aTypeRank === -1 ? 99 : aTypeRank) - (bTypeRank === -1 ? 99 : bTypeRank);
  });
  const seen = new Set();
  return orderedChecks
    .map((check) => ({
      key: `${safeStr(check?.type)}:${safeStr(check?.status)}:${safeStr(check?.message)}`,
      label: dataConsistencyShortReasonForCheck(check),
      status: safeStr(check?.status),
      type: safeStr(check?.type),
      check,
    }))
    .filter((item) => {
      if (!item.label || seen.has(item.label)) return false;
      seen.add(item.label);
      return true;
    });
}

function docCheckDetailForReason(result, reason) {
  const check = reason?.check || null;
  const type = safeStr(check?.type || reason?.type);
  const isPassedReason = safeStr(reason?.status || result?.overallStatus || result?.status) === "clear";
  const extractedData = result?.extractedData || {};
  const rows = [];

  if (type === "nationality") {
    rows.push({
      label: "KYC nationality",
      value: formatDocCheckCountry(check?.profileValue),
    });
    rows.push({
      label: "ID nationality",
      value: formatDocCheckCountry(check?.extractedValue),
    });
  } else if (type === "dateOfBirth") {
    rows.push({
      label: "KYC DOB",
      value: formatReviewDob(check?.profileValue) || safeStr(check?.profileValue) || "Not available",
    });
    rows.push({
      label: "ID DOB",
      value: formatReviewDob(check?.extractedValue) || safeStr(check?.extractedValue) || "Not available",
    });
  } else if (type === "name") {
    rows.push({
      label: "KYC name",
      value: safeStr(check?.profileValue) || "Not available",
    });
    rows.push({
      label: "ID name",
      value: formatIdFullName(check?.extractedValue) || "Not available",
    });
  } else if (type === "mrzChecksum") {
    rows.push({
      label: "MRZ checksum",
      value:
        result?.mrzChecksumValid === true
          ? "Valid"
          : result?.mrzPartialChecksumValid === true ||
            result?.mrzPrimaryFieldsChecksumValid === true
          ? "Partially verified"
          : "Failed",
    });
  } else if (type === "ocrConfidence") {
    rows.push({
      label: "OCR confidence",
      value: formatConfidence(result?.extractionConfidence),
    });
  } else if (type === "expiryDate") {
    const expiry = result?.extractedData?.expiryDate || check?.extractedValue;
    rows.push({
      label: "ID expiry date",
      value: formatReviewDob(expiry) || safeStr(expiry) || "Not available",
    });
  } else if (isPassedReason) {
    // Success details are carried by the extracted-data section below.
  }

  return {
    title: safeStr(reason?.label) || dataConsistencyTitle(result),
    documentTitle: "Identification Document",
    documentKind: "id",
    reasonTitle: safeStr(reason?.label) || dataConsistencyTitle(result),
    reasonEyebrow: isPassedReason ? "Check passed" : "Issue",
    status: safeStr(reason?.status || result?.overallStatus || result?.status),
    rows,
    sections: [
      {
        title: "Identification document extracted",
        variant: "idDocumentDetails",
        rows: [
          { label: "Full name", value: formatIdFullName(extractedData.fullName) || "Not available" },
          { label: "Date of birth", value: formatReviewDob(extractedData.dateOfBirth) || safeStr(extractedData.dateOfBirth) || "Not available" },
          { label: "Nationality", value: formatDocCheckCountry(extractedData.nationality) },
          { label: "Expiry date", value: formatReviewDob(extractedData.expiryDate) || safeStr(extractedData.expiryDate) || "Not available" },
          { label: "Issuing country", value: formatDocCheckCountry(extractedData.issuingCountry) },
        ],
      },
    ],
  };
}

function dataConsistencyShortReasons(result, fallback = "") {
  const reasons = dataConsistencyShortReasonsFromChecks(result);
  if (reasons.length) return reasons;
  const reason = dataConsistencyShortReason(result, fallback);
  return reason ? [{ key: reason, label: reason, status: safeStr(result?.overallStatus || result?.status) }] : [];
}

function shouldShowDataConsistencyBadge(result) {
  if (safeStr(result?.displayStatus) === "none") return false;
  if (isNoIdDataDetectedResult(result)) return false;
  const s = safeStr(result?.overallStatus || result?.status);
  return ["clear", "manual_review", "critical_mismatch", "pending"].includes(s);
}

function isNoIdDataDetectedResult(result) {
  if (!result) return false;
  if (safeStr(result?.overallStatus) === "no_id_data_detected") return true;
  if (safeStr(result?.idExtractionStatus) === "not_detected") return true;
  if (safeStr(result?.displayStatus) === "none") return true;
  return false;
}

function storedScanConfirmsIdRelated(result) {
  if (!result || isNoIdDataDetectedResult(result)) return false;

  const idExtractionStatus = safeStr(result?.idExtractionStatus);
  if (idExtractionStatus === "detected") return true;

  const extractedData = result?.extractedData || {};
  const hasExtractedIdentityField = [
    extractedData.fullName,
    extractedData.dateOfBirth,
    extractedData.nationality,
    extractedData.documentNumber,
    extractedData.expiryDate,
    extractedData.issuingCountry,
  ].some((value) => safeStr(value));
  if (hasExtractedIdentityField) return true;

  if (safeStr(result?.mrzFormat || extractedData?.mrzFormat)) return true;
  if (Array.isArray(result?.normalizedMrzLines) && result.normalizedMrzLines.length) {
    return true;
  }
  if (Array.isArray(result?.rawVisionMrzLines) && result.rawVisionMrzLines.length) {
    return true;
  }

  const checks = Array.isArray(result?.checks) ? result.checks : [];
  return checks.some((check) =>
    ["name", "dateOfBirth", "nationality", "expiryDate", "mrzChecksum"].includes(
      safeStr(check?.type)
    )
  );
}

function debugScanStateForNoIdEvidence(result) {
  const status = safeStr(result?.status);
  const overallStatus = safeStr(result?.overallStatus);
  const idExtractionStatus = safeStr(result?.idExtractionStatus);

  if (isNoIdDataDetectedResult(result)) {
    return {
      label: "No ID data detected",
      status: "manual_review",
    };
  }

  if (status === "pending" || overallStatus === "pending") {
    return {
      label: "ID check pending",
      status: "pending",
    };
  }

  if (status === "failed" || overallStatus === "failed" || overallStatus === "technical_error") {
    return {
      label: "ID check failed",
      status: "manual_review",
    };
  }

  if (idExtractionStatus && idExtractionStatus !== "detected") {
    return {
      label: "No ID data detected",
      status: "manual_review",
    };
  }

  return {
    label: "ID check unavailable",
    status: "manual_review",
  };
}

function todayDateOnly() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function isDocumentDataExpired(result) {
  const extractedData = result?.extractedData || {};
  const directExpiry = normalizeDocCheckDate(extractedData.expiryDate);
  const expiryCheck = Array.isArray(result?.checks)
    ? result.checks.find((check) => safeStr(check?.type) === "expiryDate")
    : null;
  const checkExpiry = normalizeDocCheckDate(expiryCheck?.extractedValue);
  const expiry = directExpiry || checkExpiry;
  return !!expiry && expiry < todayDateOnly();
}

function normalizedRequirementText(value) {
  return safeStr(value).toLowerCase().replace(/\s+/g, " ");
}

function requirementSelectionLooksLikeIdDocument(requirementKey, requirementText) {
  const key = safeStr(requirementKey).toLowerCase();
  const text = normalizedRequirementText(requirementText);
  return (
    /(^|[:_-])passport($|[:_-])/.test(key) ||
    /(^|[:_-])id($|[:_-])/.test(key) ||
    /(^|[:_-])national[_-]?id($|[:_-])/.test(key) ||
    text.includes("passport") ||
    text.includes("national id")
  );
}

function requirementSelectionLooksLikeUtilityDocument(requirementKey, requirementText) {
  const key = safeStr(requirementKey).toLowerCase();
  const text = normalizedRequirementText(requirementText);
  return (
    key.includes("proof_of_address") ||
    key.includes("proof-of-address") ||
    key.includes("poa") ||
    key.includes("utility") ||
    text.includes("proof of address") ||
    text.includes("utility bill") ||
    text.includes("utility document")
  );
}

function requirementSelectionLooksLikeCorporateDocument(requirementKey, requirementText) {
  const key = safeStr(requirementKey).toLowerCase();
  const text = normalizedRequirementText(requirementText);
  return (
    key.includes("corporate") ||
    key.includes("trade_licence") ||
    key.includes("trade-license") ||
    key.includes("trade_license") ||
    key.includes("certificate_of_incorporation") ||
    key.includes("memorandum") ||
    key.includes("moa") ||
    text.includes("trade licence") ||
    text.includes("trade license") ||
    text.includes("certificate of incorporation") ||
    text.includes("memorandum of association") ||
    text.includes("company register") ||
    text.includes("corporate registration")
  );
}

function detectedDocumentKindForRequirementSelection(file) {
  if (storedScanConfirmsIdRelated(file?.documentDataConsistencyCheck)) {
    return "id";
  }

  if (file?.proofOfAddressCheck) {
    return "utility";
  }

  if (file?.corporateDocumentCheck) {
    return "corporate";
  }

  const classification = file?.documentClassificationCheck || {};
  const classificationText = normalizedRequirementText(
    [
      classification?.documentKind,
      classification?.documentType,
      classification?.type,
      classification?.label,
    ]
      .filter(Boolean)
      .join(" ")
  );
  if (classificationText.includes("proof of address") || classificationText.includes("utility")) {
    return "utility";
  }
  if (classificationText.includes("passport") || classificationText.includes("national id")) {
    return "id";
  }
  if (classificationText.includes("corporate") || classificationText.includes("trade licence") || classificationText.includes("certificate of incorporation")) {
    return "corporate";
  }

  return "";
}

export function requirementOptionMatchesDetectedDocumentKind(file, option) {
  const kind = detectedDocumentKindForRequirementSelection(file);
  const key = safeStr(option?.key || option);
  const label = safeStr(option?.label || option);
  if (kind === "id") return requirementSelectionLooksLikeIdDocument(key, label);
  if (kind === "utility") return requirementSelectionLooksLikeUtilityDocument(key, label);
  if (kind === "corporate") return requirementSelectionLooksLikeCorporateDocument(key, label);
  return false;
}

function sortRequirementOptionsForDetectedDocument(file, options) {
  const list = Array.isArray(options) ? options : [];
  const preferred = list.find((option) =>
    requirementOptionMatchesDetectedDocumentKind(file, option)
  );
  if (!preferred) return { options: list, suggestedKey: "" };

  const preferredKey = safeStr(preferred?.key || preferred);
  return {
    options: [
      preferred,
      ...list.filter((option) => safeStr(option?.key || option) !== preferredKey),
    ],
    suggestedKey: preferredKey,
  };
}

function requirementOptionIcon(option) {
  const key = safeStr(option?.key || option);
  const label = safeStr(option?.label || option);
  if (requirementSelectionLooksLikeIdDocument(key, label)) return iconIdCardBlueDoc;
  if (requirementSelectionLooksLikeUtilityDocument(key, label)) return iconUtilityBlueDoc;
  return null;
}

function groupLooksLikeIdDocument(group, file) {
  const cardId = safeStr(file?.cardId || group?.cardId);
  if (/_id$/i.test(cardId)) return true;

  const options = Array.isArray(group?.options) ? group.options : [];
  if (
    options.some((option) => {
      const key = safeStr(option?.key).toLowerCase();
      const label = normalizedRequirementText(option?.label);
      return (
        /(^|:)id$/.test(key) ||
        label.includes("passport") ||
        label.includes("national id")
      );
    })
  ) {
    return true;
  }

  const text = normalizedRequirementText(
    [group?.title, group?.desc, file?.cardTitle].filter(Boolean).join(" ")
  );
  return text.includes("passport") || text.includes("national id");
}

function groupLooksLikeUtilityDocument(group, file) {
  const cardId = safeStr(file?.cardId || group?.cardId);
  const options = Array.isArray(group?.options) ? group.options : [];
  if (
    options.some((option) =>
      requirementSelectionLooksLikeUtilityDocument(option?.key, option?.label)
    )
  ) {
    return true;
  }

  const text = normalizedRequirementText(
    [cardId, group?.title, group?.desc, file?.cardTitle, file?.review?.requirementText]
      .filter(Boolean)
      .join(" ")
  );
  return requirementSelectionLooksLikeUtilityDocument(cardId, text);
}

function groupLooksLikeCorporateDocument(group, file) {
  const cardId = safeStr(file?.cardId || group?.cardId);
  const options = Array.isArray(group?.options) ? group.options : [];
  if (
    options.some((option) =>
      requirementSelectionLooksLikeCorporateDocument(option?.key, option?.label)
    )
  ) {
    return true;
  }

  const text = normalizedRequirementText(
    [cardId, group?.title, group?.desc, file?.cardTitle].filter(Boolean).join(" ")
  );
  return (
    requirementSelectionLooksLikeCorporateDocument(cardId, text) ||
    text.includes("commercial register") ||
    text.includes("registre du commerce") ||
    text.includes("handelsregisterauszug") ||
    text.includes("registro di commercio") ||
    text.includes("certificate of incumbency") ||
    text.includes("company registry") ||
    text.includes("register extract")
  );
}

function formatConfidence(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "n/a";
  return `${Math.round(Math.max(0, Math.min(1, n)) * 100)}%`;
}

function normalizeDocCheckName(value) {
  return safeStr(value)
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Z ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function formatTitleCaseName(value) {
  const clean = safeStr(value).replace(/\s+/g, " ").trim();
  if (!clean) return "";
  return clean
    .toLowerCase()
    .replace(/(^|[\s'’.-])([a-zÀ-ÖØ-öø-ÿ])/g, (_, separator, letter) => (
      `${separator}${letter.toUpperCase()}`
    ));
}

function formatCorporateCompanyName(value) {
  return formatTitleCaseName(value);
}

function formatIdFullName(value) {
  return formatTitleCaseName(value);
}

function nameTokensForDocCheck(value) {
  return normalizeDocCheckName(value).split(" ").filter(Boolean);
}

function nameHasCompoundSeparatorForDocCheck(value) {
  return /[A-Za-zÀ-ÖØ-öø-ÿ]\s*[-'’]\s*[A-Za-zÀ-ÖØ-öø-ÿ]/.test(safeStr(value));
}

function docCheckNameAssessment({ extractedName, profileName }) {
  const extractedTokens = nameTokensForDocCheck(extractedName);
  const profileTokens = nameTokensForDocCheck(profileName);
  if (!extractedTokens.length || !profileTokens.length) {
    return {
      status: "manual_review",
      message: "Name comparison could not be completed against the current KYC profile.",
    };
  }

  const profileFirst = profileTokens[0];
  const profileSurname = profileTokens[profileTokens.length - 1];
  const extractedFirst = extractedTokens[0];
  const extractedSurname = extractedTokens[extractedTokens.length - 1];
  const profileTokensInDocument = profileTokens.every((token) =>
    extractedTokens.includes(token)
  );
  const firstNameMatches = extractedTokens.includes(profileFirst);
  const surnameMatches = extractedTokens.includes(profileSurname);
  const exactKey = extractedTokens.join(" ") === profileTokens.join(" ");

  if (profileTokensInDocument && firstNameMatches && surnameMatches) {
    const extraTokens = extractedTokens.filter((token) => !profileTokens.includes(token));
    if (!exactKey && nameHasCompoundSeparatorForDocCheck(`${extractedName} ${profileName}`)) {
      return {
        status: "manual_review",
        message:
          "Name is close to the current KYC profile, but a compound or abbreviated name should be reviewed.",
      };
    }

    if (extraTokens.some((token) => token.length === 1)) {
      return {
        status: "manual_review",
        message:
          "Name is close to the current KYC profile, but an initial should be reviewed.",
      };
    }

    return {
      status: "match",
      message: "Name is consistent with the current KYC profile.",
    };
  }

  if (!firstNameMatches || !surnameMatches) {
    return {
      status: "critical_mismatch",
      message: !firstNameMatches && !surnameMatches
        ? "Name does not match the current KYC profile."
        : !firstNameMatches
        ? "First name differs from the current KYC profile."
        : "Surname differs from the current KYC profile.",
    };
  }

  if (extractedFirst !== profileFirst || extractedSurname !== profileSurname) {
    return {
      status: "manual_review",
      message: "Name order or structure differs from the current KYC profile.",
    };
  }

  return {
    status: "manual_review",
    message: "Name differs from the current KYC profile. Manual review required.",
  };
}

function formatDateOnlyFromStoredDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const shifted = new Date(date.getTime());
  if (shifted.getUTCHours() >= 12) {
    shifted.setUTCDate(shifted.getUTCDate() + 1);
  }
  const year = shifted.getUTCFullYear();
  const month = String(shifted.getUTCMonth() + 1).padStart(2, "0");
  const day = String(shifted.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function normalizeDocCheckDate(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return formatDateOnlyFromStoredDate(value);
  }
  if (value && typeof value.toDate === "function") {
    const d = value.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime())
      ? formatDateOnlyFromStoredDate(d)
      : "";
  }
  if (value && typeof value === "object" && Number.isFinite(Number(value.seconds))) {
    const d = new Date(Number(value.seconds) * 1000);
    return formatDateOnlyFromStoredDate(d);
  }

  const s = safeStr(value);
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const namedMonthMatch = s.match(
    /^(\d{1,2})[\s./-]+([A-Za-z]{3,9})[\s,./-]+(\d{4})$/
  );
  if (namedMonthMatch) {
    const [, dayRaw, monthRaw, yearRaw] = namedMonthMatch;
    const month = {
      jan: "01",
      feb: "02",
      mar: "03",
      apr: "04",
      may: "05",
      jun: "06",
      jul: "07",
      aug: "08",
      sep: "09",
      oct: "10",
      nov: "11",
      dec: "12",
    }[monthRaw.slice(0, 3).toLowerCase()];
    const day = String(Number(dayRaw)).padStart(2, "0");
    if (month && /^\d{4}$/.test(yearRaw) && Number(day) >= 1 && Number(day) <= 31) {
      return `${yearRaw}-${month}-${day}`;
    }
  }

  const dayMonthYearMatch = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (dayMonthYearMatch) {
    const [, dayRaw, monthRaw, yearRaw] = dayMonthYearMatch;
    const day = String(Number(dayRaw)).padStart(2, "0");
    const month = String(Number(monthRaw)).padStart(2, "0");
    if (
      Number(day) >= 1 &&
      Number(day) <= 31 &&
      Number(month) >= 1 &&
      Number(month) <= 12
    ) {
      return `${yearRaw}-${month}-${day}`;
    }
  }

  const d = new Date(s);
  return formatDateOnlyFromStoredDate(d);
}

function formatReviewDob(value) {
  const normalized = normalizeDocCheckDate(value);
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return "";
  const [, year, month, day] = match;
  const monthLabel = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ][Number(month) - 1];
  if (!monthLabel) return "";
  return `${Number(day)} ${monthLabel} ${year}`;
}

function normalizeDocCheckCountry(value) {
  const raw = safeStr(value).toUpperCase().replace(/[^A-Z]/g, "");
  if (!raw) return "";
  if (/^[A-Z]{2}$/.test(raw)) return countries.alpha2ToAlpha3(raw) || raw;
  return raw;
}

function formatDocCheckCountry(value) {
  const normalized = normalizeDocCheckCountry(value);
  const iso2 = /^[A-Z]{3}$/.test(normalized)
    ? countries.alpha3ToAlpha2(normalized)
    : /^[A-Z]{2}$/.test(normalized)
    ? normalized
    : "";
  const name = iso2 ? countries.getName(iso2, "en") : "";
  const original = safeStr(value);
  if (name && normalized) return `${name} (${normalized})`;
  return normalized || original || "Not available";
}

function firstNonEmpty(...values) {
  return values.map(safeStr).find(Boolean) || "";
}

function profileFromReviewMetaForDocCheck(reviewMeta = null) {
  if (!reviewMeta || typeof reviewMeta !== "object") return null;

  const kind = safeStr(reviewMeta.kind);
  const partyName = safeStr(reviewMeta.partyName);
  const dateOfBirth =
    reviewMeta.dateOfBirth ||
    reviewMeta.dob ||
    reviewMeta.dobOrIncorporationDate ||
    "";
  const nationality =
    kind === "entity"
      ? firstNonEmpty(reviewMeta.incorporationIso2, reviewMeta.nationalityIso2)
      : firstNonEmpty(reviewMeta.nationalityIso2, reviewMeta.incorporationIso2);

  if (!partyName && !dateOfBirth && !nationality) return null;

  return {
    fullName: partyName,
    dateOfBirth,
    nationality,
    partyId: safeStr(reviewMeta.partyId),
    partyRole: safeStr(reviewMeta.partyRole),
    partyType: kind,
    profileSource: "requirement_card",
  };
}

function currentKycProfileForDocCheck(kyc = {}, sub = {}, reviewMeta = null) {
  const customer = kyc?.customer || {};
  const personal = kyc?.personal || {};
  const identity = kyc?.identity || {};

  const fallback = {
    fullName: firstNonEmpty(
      customer.name,
      personal.fullName,
      identity.fullName,
      sub.clientName,
      sub.name,
      sub.fullName
    ),
    dateOfBirth:
      customer.dobOrIncorporationDate ||
      customer.dateOfBirth ||
      customer.dob ||
      personal.dobOrIncorporationDate ||
      personal.dateOfBirth ||
      personal.dob ||
      identity.dobOrIncorporationDate ||
      identity.dateOfBirth ||
      sub.dobOrIncorporationDate ||
      sub.dateOfBirth ||
      sub.dob ||
      "",
    nationality: firstNonEmpty(
      customer.nationalityOrIncorporationCountry,
      customer.nationality,
      customer.nationalityIso3,
      personal.nationality,
      identity.nationality,
      sub.nationality
    ),
    partyId: "customer",
    partyRole: "",
    partyType: normPartyType(customer.type) || "individual",
    profileSource: "customer_profile",
  };

  const metaProfile = profileFromReviewMetaForDocCheck(reviewMeta);
  if (!metaProfile) return fallback;

  if (metaProfile.partyId && metaProfile.partyId !== "customer") {
    return metaProfile;
  }

  return {
    ...fallback,
    ...metaProfile,
    fullName: metaProfile.fullName || fallback.fullName,
    dateOfBirth: metaProfile.dateOfBirth || fallback.dateOfBirth,
    nationality: metaProfile.nationality || fallback.nationality,
    partyType: metaProfile.partyType || fallback.partyType,
  };
}

function isNonCustomerIndividualProfile(profile = {}) {
  return (
    safeStr(profile?.partyId) &&
    safeStr(profile?.partyId) !== "customer" &&
    normPartyType(profile?.partyType) === "individual"
  );
}

function reviewMetaLooksNonCustomerIndividual(reviewMeta = null) {
  const partyId = safeStr(reviewMeta?.partyId);
  if (!partyId || partyId === "customer") return false;
  if (normPartyType(reviewMeta?.kind) === "individual") return true;
  const role = safeStr(reviewMeta?.partyRole).toLowerCase();
  return (
    role.includes("ubo") ||
    role.includes("direct shareholder") ||
    role.includes("signatory") ||
    role.includes("trustee") ||
    role.includes("trust party")
  );
}

function idDataUpdateOptionsFromFile(file, kyc = {}, sub = {}, reviewMeta = null) {
  const metaPartyId = safeStr(reviewMeta?.partyId);
  const isCustomerParty = !metaPartyId || metaPartyId === "customer";
  const isNonCustomerIndividual = reviewMetaLooksNonCustomerIndividual(reviewMeta);

  const result = file?.documentDataConsistencyCheck || {};
  const extractedData = result?.extractedData || {};
  const checks = Array.isArray(result?.checks) ? result.checks : [];
  const checkForType = (type) => checks.find((check) => safeStr(check?.type) === type) || null;
  const nameCheck = checkForType("name");
  const dobCheck = checkForType("dateOfBirth");
  const nationalityCheck = checkForType("nationality");
  const profile = currentKycProfileForDocCheck(kyc, sub, reviewMeta);
  const dob = normalizeDocCheckDate(extractedData.dateOfBirth || dobCheck?.extractedValue);
  const profileDob = normalizeDocCheckDate(profile.dateOfBirth);
  const nationality = normalizeDocCheckCountry(extractedData.nationality || nationalityCheck?.extractedValue);
  const profileNationality = normalizeDocCheckCountry(profile.nationality);
  const rawFullName = safeStr(extractedData.fullName || nameCheck?.extractedValue);
  const fullName = formatIdFullName(rawFullName);
  const profileFullName = safeStr(profile.fullName);
  const nationalityIso2 =
    /^[A-Z]{3}$/.test(nationality) ? countries.alpha3ToAlpha2(nationality) || "" : nationality;
  const options = [];
  const dobUpdateTarget = isCustomerParty ? { kind: "customer" } : partyDobUpdateTarget(reviewMeta);
  const roleLabel = isCustomerParty ? "KYC" : shortPartyRoleLabel(reviewMeta);

  if (
    isCustomerParty &&
    fullName &&
    profileFullName &&
    normalizeDocCheckName(fullName) !== normalizeDocCheckName(profileFullName)
  ) {
    options.push({
      key: "fullName",
      label: `Update full name in KYC profile: ${profileFullName} → ${fullName}`,
      value: fullName,
    });
  }

  if (
    !isCustomerParty &&
    isNonCustomerIndividual &&
    fullName &&
    profileFullName &&
    normalizeDocCheckName(fullName) !== normalizeDocCheckName(profileFullName) &&
    dobUpdateTarget
  ) {
    options.push({
      key: "partyName",
      label: `Update name in ${roleLabel} profile: ${profileFullName} → ${fullName}`,
      value: fullName,
      updateTarget: dobUpdateTarget,
    });
  }

  if ((isCustomerParty || isNonCustomerIndividual) && dob && !profileDob && dobUpdateTarget) {
    options.push({
      key: "dob",
      label: isCustomerParty
        ? `Add DOB to KYC profile: ${formatReviewDob(dob) || dob}`
        : `Add DOB to ${roleLabel} profile: ${formatReviewDob(dob) || dob}`,
      value: dob,
      updateTarget: dobUpdateTarget,
    });
  }

  if ((isCustomerParty || isNonCustomerIndividual) && dob && profileDob && dob !== profileDob && dobUpdateTarget) {
    options.push({
      key: "dob",
      label: isCustomerParty
        ? `Update DOB in KYC profile: ${formatReviewDob(profileDob) || profileDob} → ${formatReviewDob(dob) || dob}`
        : `Update DOB in ${roleLabel} profile: ${formatReviewDob(profileDob) || profileDob} → ${formatReviewDob(dob) || dob}`,
      value: dob,
      updateTarget: dobUpdateTarget,
    });
  }

  if (isCustomerParty && nationality && !profileNationality) {
    options.push({
      key: "nationality",
      label: `Add nationality to KYC profile: ${nationality}`,
      value: nationality,
      kycValue: nationalityIso2 || nationality,
    });
  }

  if (isCustomerParty && nationality && profileNationality && nationality !== profileNationality) {
    options.push({
      key: "nationality",
      label: `Update nationality in KYC profile: ${profileNationality} → ${nationality}`,
      value: nationality,
      kycValue: nationalityIso2 || nationality,
    });
  }

  if (!isCustomerParty && isNonCustomerIndividual && nationality && !profileNationality && dobUpdateTarget) {
    options.push({
      key: "partyNationality",
      label: `Add nationality to ${roleLabel} profile: ${formatDocCheckCountry(nationality)}`,
      value: nationality,
      kycValue: nationalityIso2 || nationality,
      updateTarget: dobUpdateTarget,
    });
  }

  if (!isCustomerParty && isNonCustomerIndividual && nationality && profileNationality && nationality !== profileNationality && dobUpdateTarget) {
    options.push({
      key: "partyNationality",
      label: `Update nationality in ${roleLabel} profile: ${formatDocCheckCountry(profileNationality)} → ${formatDocCheckCountry(nationality)}`,
      value: nationality,
      kycValue: nationalityIso2 || nationality,
      updateTarget: dobUpdateTarget,
    });
  }

  return options;
}

function proofOfAddressUpdateOptionsFromFile(file, kyc = {}, sub = {}, reviewMeta = null) {
  const result = file?.proofOfAddressCheck || {};
  if (!file?.proofOfAddressCheck) return [];

  const profile = currentKycProfileForDocCheck(kyc, sub, reviewMeta);
  const isCustomerParty = !safeStr(reviewMeta?.partyId) || safeStr(reviewMeta?.partyId) === "customer";
  const isIndividualParty =
    normPartyType(profile?.partyType) === "individual" ||
    normPartyType(reviewMeta?.kind) === "individual" ||
    (isCustomerParty && normPartyType(kyc?.customer?.type || "individual") === "individual");
  if (!isIndividualParty) return [];

  const checks = Array.isArray(result?.checks) ? result.checks : [];
  const checkForType = (type) => checks.find((check) => safeStr(check?.type) === type) || null;
  const nameCheck = checkForType("name_match");
  const addressCountryCheck =
    checkForType("address_country_match") || checkForType("country_match") || null;
  const issuerCountryCheck = checkForType("issuer_country_match");
  const domicileCheck = addressCountryCheck || issuerCountryCheck;
  const extracted = result?.extracted || {};
  const expected = result?.expectedParty || {};
  const updateTarget = isCustomerParty ? { kind: "customer" } : partyDobUpdateTarget(reviewMeta);
  const roleLabel = isCustomerParty ? "KYC" : shortPartyRoleLabel(reviewMeta);
  const options = [];

  const documentName = formatIdFullName(
    nameCheck?.extractedValue || extracted.client_name || extracted.name || extracted.fullName
  );
  const profileName = safeStr(
    nameCheck?.expectedValue || expected.partyName || profile.fullName
  );
  const nameStatus = safeStr(nameCheck?.status);
  if (
    updateTarget &&
    documentName &&
    profileName &&
    nameStatus !== "pass" &&
    nameStatus !== "match" &&
    normalizeDocCheckName(documentName) !== normalizeDocCheckName(profileName)
  ) {
    options.push({
      key: isCustomerParty ? "poaCustomerName" : "poaPartyName",
      label: isCustomerParty
        ? `Update full name in KYC profile: ${profileName} → ${documentName}`
        : `Update name in ${roleLabel} profile: ${profileName} → ${documentName}`,
      value: documentName,
      updateTarget,
    });
  }

  const documentDomicile = normalizeDocCheckCountry(
    domicileCheck?.extractedValue ||
      extracted.country_iso2 ||
      extracted.country ||
      extracted.address_country_iso2 ||
      extracted.issuer_country_iso2
  );
  const profileDomicile = normalizeDocCheckCountry(
    domicileCheck?.expectedValue ||
      expected.domicileIso2 ||
      profile.domicile ||
      profile.countryOfResidence ||
      (isCustomerParty ? kyc?.customer?.country : "")
  );
  const domicileStatus = safeStr(domicileCheck?.status);
  const domicileIso2 =
    /^[A-Z]{3}$/.test(documentDomicile)
      ? countries.alpha3ToAlpha2(documentDomicile) || ""
      : /^[A-Z]{2}$/.test(documentDomicile)
      ? documentDomicile
      : "";
  if (
    updateTarget &&
    documentDomicile &&
    domicileStatus !== "pass" &&
    domicileStatus !== "match" &&
    (!profileDomicile || documentDomicile !== profileDomicile)
  ) {
    options.push({
      key: isCustomerParty ? "poaCustomerDomicile" : "poaPartyDomicile",
      label: isCustomerParty
        ? `${profileDomicile ? "Update" : "Add"} domicile in KYC profile: ${
            profileDomicile ? `${formatDocCheckCountry(profileDomicile)} → ` : ""
          }${formatDocCheckCountry(documentDomicile)}`
        : `${profileDomicile ? "Update" : "Add"} domicile in ${roleLabel} profile: ${
            profileDomicile ? `${formatDocCheckCountry(profileDomicile)} → ` : ""
          }${formatDocCheckCountry(documentDomicile)}`,
      value: documentDomicile,
      kycValue: domicileIso2 || documentDomicile,
      updateTarget,
    });
  }

  return options;
}

function corporateDataUpdateOptionsFromFile(file, kyc = {}, sub = {}, reviewMeta = null) {
  const result = file?.corporateDocumentCheck || {};
  const extracted = result?.extracted || {};
  const expected = result?.expectedParty || {};
  const customer = kyc?.customer || {};
  if (!file?.corporateDocumentCheck) return [];

  const options = [];
  const checks = Array.isArray(result?.checks) ? result.checks : [];
  const checkForType = (type) => checks.find((check) => safeStr(check?.type) === type) || null;
  const checkIsNonPassing = (check) => {
    const status = safeStr(check?.status);
    return !!check && status !== "pass" && status !== "passed" && status !== "match" && status !== "skipped";
  };
  const nameCheck = checkForType("corporate_company_name_match");
  const dateCheck = checkForType("corporate_incorporation_date_match");
  const countryCheck = checkForType("corporate_country_match");
  const rawDocumentName = safeStr(nameCheck?.extractedValue || extracted.company_name);
  const documentName = formatCorporateCompanyName(rawDocumentName);
  const profileName = safeStr(nameCheck?.expectedValue || expected.partyName || customer.name || sub.clientName || sub.name);
  const documentDate = normalizeDocCheckDate(dateCheck?.extractedValue || extracted.incorporation_date);
  const profileDate = normalizeDocCheckDate(
    dateCheck?.expectedValue || expected.incorporationDate || customer.dobOrIncorporationDate
  );
  const documentCountry = normalizeDocCheckCountry(
    countryCheck?.extractedValue ||
      extracted.jurisdiction_country_iso2 ||
      extracted.jurisdiction_country ||
      extracted.issuer_country_iso2 ||
      extracted.issuer_country
  );
  const profileCountry = normalizeDocCheckCountry(
    countryCheck?.expectedValue || expected.incorporationIso2 || customer.nationalityOrIncorporationCountry
  );
  const documentCountryIso2 =
    /^[A-Z]{3}$/.test(documentCountry)
      ? countries.alpha3ToAlpha2(documentCountry) || ""
      : /^[A-Z]{2}$/.test(documentCountry)
      ? documentCountry
      : "";

  if (
    documentName &&
    profileName &&
    (checkIsNonPassing(nameCheck) || normalizeDocCheckName(documentName) !== normalizeDocCheckName(profileName))
  ) {
    options.push({
      key: "corporateName",
      label: `Update company name in KYC profile: ${profileName} → ${documentName}`,
      value: documentName,
    });
  }

  if (documentDate && profileDate && (checkIsNonPassing(dateCheck) || documentDate !== profileDate)) {
    options.push({
      key: "corporateIncorporationDate",
      label: `Update incorporation date in KYC profile: ${formatReviewDob(profileDate) || profileDate} → ${formatReviewDob(documentDate) || documentDate}`,
      value: documentDate,
    });
  }

  if (documentCountry && profileCountry && (checkIsNonPassing(countryCheck) || documentCountry !== profileCountry)) {
    options.push({
      key: "corporateIncorporationCountry",
      label: `Update incorporation country in KYC profile: ${formatDocCheckCountry(profileCountry)} → ${formatDocCheckCountry(documentCountry)}`,
      value: documentCountry,
      kycValue: documentCountryIso2 || documentCountry,
    });
  }

  return options;
}

function dateOnlyToFirestoreDate(value) {
  const normalized = normalizeDocCheckDate(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return null;
  const date = new Date(`${normalized}T00:00:00.000Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function shortPartyRoleLabel(reviewMeta = null) {
  const role = safeStr(reviewMeta?.partyRole);
  const lower = role.toLowerCase();
  if (lower.includes("ubo")) return "UBO";
  if (lower.includes("direct shareholder")) return "direct shareholder";
  if (lower.includes("signatory")) return "signatory";
  if (lower.includes("trustee")) return "trustee";
  if (lower.includes("trust")) return "trust party";
  return "party";
}

function partyDobUpdateTarget(reviewMeta = null) {
  const partyId = safeStr(reviewMeta?.partyId);
  if (!partyId || partyId === "customer") return { kind: "customer" };
  const parts = partyId.split(":");
  const type = safeStr(parts[0]);
  if (type === "shareholder") {
    return { kind: "arrayRow", collection: "shareholders", rowId: parts.slice(1).join(":") };
  }
  if (type === "ubo") {
    return { kind: "arrayRow", collection: "ubos", rowId: parts.slice(1).join(":") };
  }
  if (type === "signatory") {
    return { kind: "arrayRow", collection: "signatories", rowId: parts.slice(1).join(":") };
  }
  if (type === "trustee") {
    return {
      kind: "legalArrangementParty",
      arrangementId: safeStr(parts[1]),
      partyId: parts.slice(2).join(":"),
    };
  }
  const role = safeStr(reviewMeta?.partyRole).toLowerCase();
  const fallbackRowId = safeStr(reviewMeta?.partyName);
  if (!fallbackRowId) return null;
  if (role.includes("ubo")) {
    return { kind: "arrayRow", collection: "ubos", rowId: fallbackRowId };
  }
  if (role.includes("direct shareholder")) {
    return { kind: "arrayRow", collection: "shareholders", rowId: fallbackRowId };
  }
  if (role.includes("signatory")) {
    return { kind: "arrayRow", collection: "signatories", rowId: fallbackRowId };
  }
  return null;
}

function rowMatchesId(row, rowId) {
  const cleanId = safeStr(rowId);
  if (!cleanId) return false;
  const cleanIdLower = cleanId.toLowerCase();
  return [row?._rowId, row?._docRef, row?.name].some((value) => {
    const candidate = safeStr(value);
    return candidate === cleanId || candidate.toLowerCase() === cleanIdLower;
  });
}

function updateOwnershipArrayRowDate(ownership = {}, collection, rowId, date) {
  const list = Array.isArray(ownership?.[collection]) ? ownership[collection] : [];
  let changed = false;
  const next = list.map((row) => {
    if (!rowMatchesId(row, rowId)) return row;
    changed = true;
    return { ...(row || {}), dobOrIncorporationDate: date };
  });
  return changed ? next : null;
}

function updateOwnershipArrayRowFields(ownership = {}, collection, rowId, fields = {}) {
  const list = Array.isArray(ownership?.[collection]) ? ownership[collection] : [];
  let changed = false;
  const next = list.map((row) => {
    if (!rowMatchesId(row, rowId)) return row;
    changed = true;
    return { ...(row || {}), ...(fields || {}) };
  });
  return changed ? next : null;
}

function relatedShareholderIdsForUboRows(rows = [], rowId = "") {
  const ids = new Set();
  const cleanRowId = safeStr(rowId);
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!rowMatchesId(row, cleanRowId)) return;
    [
      row?.relatedShareholderRowId,
      row?.relatedShareholderNodeId,
      row?.treeNodeId,
      row?.subjectTreeNodeId,
      row?.subjectNodeId,
      row?.name,
    ].forEach((value) => {
      const clean = safeStr(value);
      if (clean) ids.add(clean);
    });
  });
  if (cleanRowId) ids.add(cleanRowId);
  return Array.from(ids);
}

function updateMatchingDirectShareholderDobForUbo(ownership = {}, uboRowId = "", date) {
  const shareholderTargets = relatedShareholderIdsForUboRows(ownership?.ubos, uboRowId);
  if (!shareholderTargets.length) return null;

  const shareholders = Array.isArray(ownership?.shareholders) ? ownership.shareholders : [];
  let changed = false;
  const nextShareholders = shareholders.map((row) => {
    const matches = shareholderTargets.some((target) => {
      const cleanTarget = safeStr(target);
      if (!cleanTarget) return false;
      return (
        rowMatchesId(row, cleanTarget) ||
        safeStr(row?.treeNodeId) === cleanTarget ||
        safeStr(row?.subjectNodeId) === cleanTarget
      );
    });
    if (!matches) return row;
    changed = true;
    return { ...(row || {}), dobOrIncorporationDate: date };
  });

  return changed ? nextShareholders : null;
}

function updateMatchingDirectShareholderFieldsForUbo(ownership = {}, uboRowId = "", fields = {}) {
  const shareholderTargets = relatedShareholderIdsForUboRows(ownership?.ubos, uboRowId);
  if (!shareholderTargets.length) return null;

  const shareholders = Array.isArray(ownership?.shareholders) ? ownership.shareholders : [];
  let changed = false;
  const nextShareholders = shareholders.map((row) => {
    const matches = shareholderTargets.some((target) => {
      const cleanTarget = safeStr(target);
      if (!cleanTarget) return false;
      return (
        rowMatchesId(row, cleanTarget) ||
        safeStr(row?.treeNodeId) === cleanTarget ||
        safeStr(row?.subjectNodeId) === cleanTarget
      );
    });
    if (!matches) return row;
    changed = true;
    return { ...(row || {}), ...(fields || {}) };
  });

  return changed ? nextShareholders : null;
}

function updateLegalArrangementPartyDate(ownership = {}, target, date) {
  const arrangements = Array.isArray(ownership?.legalArrangements?.arrangements)
    ? ownership.legalArrangements.arrangements
    : [];
  let changed = false;
  const nextArrangements = arrangements.map((arr, idx) => {
    const arrangementMatches =
      rowMatchesId(arr, target?.arrangementId) || safeStr(target?.arrangementId) === String(idx);
    if (!arrangementMatches) return arr;
    const parties = Array.isArray(arr?.parties) ? arr.parties : [];
    let partyChanged = false;
    const nextParties = parties.map((party) => {
      if (!rowMatchesId(party, target?.partyId)) return party;
      partyChanged = true;
      changed = true;
      return { ...(party || {}), dobOrIncorporationDate: date };
    });
    return partyChanged ? { ...(arr || {}), parties: nextParties } : arr;
  });
  return changed ? nextArrangements : null;
}

function updateLegalArrangementPartyFields(ownership = {}, target, fields = {}) {
  const arrangements = Array.isArray(ownership?.legalArrangements?.arrangements)
    ? ownership.legalArrangements.arrangements
    : [];
  let changed = false;
  const nextArrangements = arrangements.map((arr, idx) => {
    const arrangementMatches =
      rowMatchesId(arr, target?.arrangementId) || safeStr(target?.arrangementId) === String(idx);
    if (!arrangementMatches) return arr;
    const parties = Array.isArray(arr?.parties) ? arr.parties : [];
    let partyChanged = false;
    const nextParties = parties.map((party) => {
      if (!rowMatchesId(party, target?.partyId)) return party;
      partyChanged = true;
      changed = true;
      return { ...(party || {}), ...(fields || {}) };
    });
    return partyChanged ? { ...(arr || {}), parties: nextParties } : arr;
  });
  return changed ? nextArrangements : null;
}

async function updateKycProfileFromDocumentData({ clientId, options, selected, kyc }) {
  const cleanClientId = safeStr(clientId);
  if (!cleanClientId) return;
  const selectedOptions = (Array.isArray(options) ? options : []).filter(
    (option) => selected?.[option.key]
  );
  if (!selectedOptions.length) return;

  const updates = {
    updatedAt: serverTimestamp(),
  };
  const ownership = kyc?.ownership || {};

  selectedOptions.forEach((option) => {
    if (option.key === "fullName") {
      const name = formatIdFullName(option.value);
      if (name) updates["customer.name"] = name;
    }
    if (option.key === "dob") {
      const date = dateOnlyToFirestoreDate(option.value);
      if (!date) return;
      const target = option.updateTarget || { kind: "customer" };
      if (target.kind === "customer") {
        updates["customer.dobOrIncorporationDate"] = date;
      } else if (target.kind === "arrayRow") {
        const nextRows = updateOwnershipArrayRowDate(
          ownership,
          target.collection,
          target.rowId,
          date
        );
        if (nextRows) updates[`ownership.${target.collection}`] = nextRows;
        if (target.collection === "ubos") {
          const nextShareholders = updateMatchingDirectShareholderDobForUbo(
            ownership,
            target.rowId,
            date
          );
          if (nextShareholders) updates["ownership.shareholders"] = nextShareholders;
        }
      } else if (target.kind === "legalArrangementParty") {
        const nextArrangements = updateLegalArrangementPartyDate(ownership, target, date);
        if (nextArrangements) {
          updates["ownership.legalArrangements.arrangements"] = nextArrangements;
        }
      }
    }
    if (option.key === "partyName") {
      const name = formatIdFullName(option.value);
      const target = option.updateTarget;
      if (!name || !target) return;
      if (target.kind === "arrayRow") {
        const nextRows = updateOwnershipArrayRowFields(
          ownership,
          target.collection,
          target.rowId,
          { name }
        );
        if (nextRows) {
          updates[`ownership.${target.collection}`] = nextRows;
        } else if (target.collection === "signatories") {
          const nextControllers = updateOwnershipArrayRowFields(
            ownership,
            "controllers",
            target.rowId,
            { name }
          );
          if (nextControllers) updates["ownership.controllers"] = nextControllers;
        }
        if (target.collection === "ubos") {
          const nextShareholders = updateMatchingDirectShareholderFieldsForUbo(
            ownership,
            target.rowId,
            { name }
          );
          if (nextShareholders) updates["ownership.shareholders"] = nextShareholders;
        }
      } else if (target.kind === "legalArrangementParty") {
        const nextArrangements = updateLegalArrangementPartyFields(
          ownership,
          target,
          { name }
        );
        if (nextArrangements) {
          updates["ownership.legalArrangements.arrangements"] = nextArrangements;
        }
      }
    }
    if (option.key === "nationality") {
      const nationality = safeStr(option.kycValue || option.value).toUpperCase();
      if (nationality) updates["customer.nationalityOrIncorporationCountry"] = nationality;
    }
    if (option.key === "partyNationality") {
      const nationality = safeStr(option.kycValue || option.value).toUpperCase();
      const target = option.updateTarget;
      if (!nationality || !target) return;
      if (target.kind === "arrayRow") {
        const nextRows = updateOwnershipArrayRowFields(
          ownership,
          target.collection,
          target.rowId,
          { nationality }
        );
        if (nextRows) {
          updates[`ownership.${target.collection}`] = nextRows;
        } else if (target.collection === "signatories") {
          const nextControllers = updateOwnershipArrayRowFields(
            ownership,
            "controllers",
            target.rowId,
            { nationality }
          );
          if (nextControllers) updates["ownership.controllers"] = nextControllers;
        }
        if (target.collection === "ubos") {
          const nextShareholders = updateMatchingDirectShareholderFieldsForUbo(
            ownership,
            target.rowId,
            { nationality }
          );
          if (nextShareholders) updates["ownership.shareholders"] = nextShareholders;
        }
      } else if (target.kind === "legalArrangementParty") {
        const nextArrangements = updateLegalArrangementPartyFields(
          ownership,
          target,
          { nationality }
        );
        if (nextArrangements) {
          updates["ownership.legalArrangements.arrangements"] = nextArrangements;
        }
      }
    }
    if (option.key === "poaCustomerName") {
      const name = formatIdFullName(option.value);
      if (name) updates["customer.name"] = name;
    }
    if (option.key === "poaPartyName") {
      const name = formatIdFullName(option.value);
      const target = option.updateTarget;
      if (!name || !target) return;
      if (target.kind === "arrayRow") {
        const nextRows = updateOwnershipArrayRowFields(
          ownership,
          target.collection,
          target.rowId,
          { name }
        );
        if (nextRows) {
          updates[`ownership.${target.collection}`] = nextRows;
        } else if (target.collection === "signatories") {
          const nextControllers = updateOwnershipArrayRowFields(
            ownership,
            "controllers",
            target.rowId,
            { name }
          );
          if (nextControllers) updates["ownership.controllers"] = nextControllers;
        }
        if (target.collection === "ubos") {
          const nextShareholders = updateMatchingDirectShareholderFieldsForUbo(
            ownership,
            target.rowId,
            { name }
          );
          if (nextShareholders) updates["ownership.shareholders"] = nextShareholders;
        }
      } else if (target.kind === "legalArrangementParty") {
        const nextArrangements = updateLegalArrangementPartyFields(
          ownership,
          target,
          { name }
        );
        if (nextArrangements) {
          updates["ownership.legalArrangements.arrangements"] = nextArrangements;
        }
      }
    }
    if (option.key === "poaCustomerDomicile") {
      const country = safeStr(option.kycValue || option.value).toUpperCase();
      if (country) updates["customer.country"] = country;
    }
    if (option.key === "poaPartyDomicile") {
      const country = safeStr(option.kycValue || option.value).toUpperCase();
      const target = option.updateTarget;
      if (!country || !target) return;
      if (target.kind === "arrayRow") {
        const nextRows = updateOwnershipArrayRowFields(
          ownership,
          target.collection,
          target.rowId,
          { countryOfResidence: country }
        );
        if (nextRows) {
          updates[`ownership.${target.collection}`] = nextRows;
        } else if (target.collection === "signatories") {
          const nextControllers = updateOwnershipArrayRowFields(
            ownership,
            "controllers",
            target.rowId,
            { countryOfResidence: country }
          );
          if (nextControllers) updates["ownership.controllers"] = nextControllers;
        }
        if (target.collection === "ubos") {
          const nextShareholders = updateMatchingDirectShareholderFieldsForUbo(
            ownership,
            target.rowId,
            { countryOfResidence: country }
          );
          if (nextShareholders) updates["ownership.shareholders"] = nextShareholders;
        }
      } else if (target.kind === "legalArrangementParty") {
        const nextArrangements = updateLegalArrangementPartyFields(
          ownership,
          target,
          { domicile: country }
        );
        if (nextArrangements) {
          updates["ownership.legalArrangements.arrangements"] = nextArrangements;
        }
      }
    }
    if (option.key === "corporateName") {
      const name = formatCorporateCompanyName(option.value);
      if (name) updates["customer.name"] = name;
    }
    if (option.key === "corporateIncorporationDate") {
      const date = dateOnlyToFirestoreDate(option.value);
      if (date) updates["customer.dobOrIncorporationDate"] = date;
    }
    if (option.key === "corporateIncorporationCountry") {
      const country = safeStr(option.kycValue || option.value).toUpperCase();
      if (country) updates["customer.nationalityOrIncorporationCountry"] = country;
    }
  });

  const safeUpdates = sanitizeFirestoreUpdatePayload(updates);
  if (Object.keys(safeUpdates).length <= 1) return;
  await updateDoc(doc(db, "kyc", cleanClientId), safeUpdates);
}

function statusLabelForDocCheckMessage(status) {
  if (status === "match") return "OK";
  if (status === "critical_mismatch") return "Critical";
  if (status === "manual_review") return "Review";
  return "Info";
}

function docCheckMessage(check) {
  return `${statusLabelForDocCheckMessage(check.status)} - ${check.label}: ${check.message}`;
}

function logDocCheckCardContent({ result, file, group, sub, kycProfile }) {
  if (!result || typeof window === "undefined") return;
  const payload = {
    fileName: file?.originalName || "",
    objectPath: file?.objectPath || result.sourceObjectPath || result.storagePath || "",
    card: {
      title: group?.title || "",
      cardId: group?.cardId || file?.cardId || "",
    },
    status: {
      status: result.status || "",
      overallStatus: result.overallStatus || "",
      title: dataConsistencyTitle(result),
      shortReason: dataConsistencyShortReason(result),
      shortReasons: dataConsistencyShortReasons(result).map((reason) => reason.label),
      messages: result.messages || [],
    },
    extraction: {
      extractedData: result.extractedData || null,
      mrzChecksumValid: result.mrzChecksumValid,
      extractionConfidence: result.extractionConfidence,
      ocr: formatConfidence(result.extractionConfidence),
      mrzFormat: result.mrzFormat || result.extractedData?.mrzFormat || "",
      normalizedMrzLines: result.normalizedMrzLines || [],
      rawVisionMrzLines: result.rawVisionMrzLines || [],
      mrzCorrection: result.mrzCorrection || null,
    },
    comparison: {
      currentKycProfile: kycProfile || null,
      checks: result.checks || [],
      comparedIn: result.liveComparison ? "react_review_page" : "stored_backend_result",
    },
    submission: {
      id: sub?.id || "",
      clientId: sub?.clientId || "",
      orgId: sub?.orgId || "",
    },
  };
  window.__mykycDocCheckCardContent = payload;
}

function compareStoredExtractionWithCurrentKyc(result, kycLive, sub, reviewMeta = null) {
  if (!result || safeStr(result.status) === "pending") return result;
  if (safeStr(result.status) === "failed") return result;
  if (isNoIdDataDetectedResult(result)) {
    return result;
  }

  const extractedData = result.extractedData || {};
  const confidence = Number(result.extractionConfidence);
  const extractionReliable =
    result.mrzChecksumValid === true && Number.isFinite(confidence) && confidence >= 0.65;
  if (!extractionReliable) {
    return {
      ...result,
      liveComparison: true,
      overallStatus: "manual_review",
      checks: result.checks || [],
      messages:
        Array.isArray(result.messages) && result.messages.length
          ? result.messages
          : ["MRZ extraction is not reliable enough for profile comparison. Please review manually."],
    };
  }

  const profile = currentKycProfileForDocCheck(kycLive || {}, sub || {}, reviewMeta);
  const checks = [];
  const messages = [];

  const extractedName = normalizeDocCheckName(extractedData.fullName);
  const profileName = normalizeDocCheckName(profile.fullName);
  const extractedDob = normalizeDocCheckDate(extractedData.dateOfBirth);
  const profileDob = normalizeDocCheckDate(profile.dateOfBirth);
  const extractedNat = normalizeDocCheckCountry(extractedData.nationality);
  const profileNat = normalizeDocCheckCountry(profile.nationality);
  const expiry = normalizeDocCheckDate(extractedData.expiryDate);

  if (extractedName && profileName) {
    const nameAssessment = docCheckNameAssessment({
      extractedName: extractedData.fullName,
      profileName: profile.fullName,
    });
    checks.push({
      type: "name",
      label: "Name",
      status: nameAssessment.status,
      message: nameAssessment.message,
      extractedValue: extractedData.fullName,
      profileValue: profile.fullName,
    });
    if (nameAssessment.status !== "match") {
      messages.push(nameAssessment.message);
    }
  } else {
    checks.push({
      type: "name",
      label: "Name",
      status: "manual_review",
      message: "Name comparison could not be completed against the current KYC profile.",
      extractedValue: extractedData.fullName,
      profileValue: profile.fullName,
    });
  }

  if (extractedDob && profileDob) {
    const dobMatch = extractedDob === profileDob;
    checks.push({
      type: "dateOfBirth",
      label: "Date of birth",
      status: dobMatch ? "match" : "critical_mismatch",
      message: dobMatch
        ? "Date of birth matches the current KYC profile."
        : "Date of birth differs from the current KYC profile.",
      extractedValue: extractedDob,
      profileValue: profileDob,
    });
    if (!dobMatch) messages.push("The date of birth differs from the current KYC profile.");
  } else if (extractedDob && !profileDob && isNonCustomerIndividualProfile(profile)) {
    checks.push({
      type: "dateOfBirth",
      label: "Date of birth",
      status: "match",
      message: "Date of birth was read from the ID and can be recorded on approval.",
      extractedValue: extractedDob,
      profileValue: profileDob,
    });
  } else {
    checks.push({
      type: "dateOfBirth",
      label: "Date of birth",
      status: "manual_review",
      message: "Date of birth comparison could not be completed against the current KYC profile.",
      extractedValue: extractedDob,
      profileValue: profileDob,
    });
  }

  if (extractedNat && profileNat) {
    const natMatch = extractedNat === profileNat;
    checks.push({
      type: "nationality",
      label: "Nationality",
      status: natMatch ? "match" : "critical_mismatch",
      message: natMatch
        ? "Nationality is consistent with the current KYC profile."
        : "Nationality differs from the current KYC profile.",
      extractedValue: extractedData.nationality,
      profileValue: profile.nationality,
    });
    if (!natMatch) messages.push("The nationality differs from the current KYC profile.");
  } else {
    checks.push({
      type: "nationality",
      label: "Nationality",
      status: "manual_review",
      message: "Nationality comparison could not be completed against the current KYC profile.",
      extractedValue: extractedData.nationality,
      profileValue: profile.nationality,
    });
  }

  const expired = expiry && new Date(`${expiry}T23:59:59.999Z`).getTime() < Date.now();
  checks.push({
    type: "expiryDate",
    label: "Expiry date",
    status: expired ? "expired" : "match",
    message: expired ? "The document is expired." : "The document is not expired.",
    extractedValue: extractedData.expiryDate,
  });

  checks.push({
    type: "mrzChecksum",
    label: "MRZ checksum",
    status: result.mrzChecksumValid ? "match" : "manual_review",
    message: result.mrzChecksumValid
      ? "MRZ check digits are valid."
      : result.mrzPartialChecksumValid || result.mrzPrimaryFieldsChecksumValid
      ? "MRZ identity fields are verified, but the final composite checksum failed."
      : "MRZ check digits failed. This may be an OCR or document quality issue.",
  });

  const lowConfidence = !Number.isFinite(confidence) || confidence < 0.75;
  checks.push({
    type: "ocrConfidence",
    label: "OCR confidence",
    status: lowConfidence ? "manual_review" : "match",
    message: lowConfidence
      ? "OCR confidence is low. Manual review required."
      : "OCR confidence is acceptable.",
    extractedValue: result.extractionConfidence,
  });
  if (lowConfidence) messages.push("OCR confidence is low.");

  let overallStatus = "clear";
  if (checks.some((check) => check.status === "critical_mismatch")) {
    overallStatus = "critical_mismatch";
  } else if (checks.some((check) => check.status === "manual_review")) {
    overallStatus = "manual_review";
  }

  return {
    ...result,
    liveComparison: true,
    liveComparedAt: new Date().toISOString(),
    clientProfileCompared: profile,
    overallStatus,
    checks,
    messages: messages.length
      ? [...new Set([...messages, ...checks.filter((check) => check.status !== "match").map(docCheckMessage)])]
      : [
          `Document data is consistent with the current KYC profile. Checked: ${checks
            .filter((check) => check.status === "match")
            .map((check) => check.label.toLowerCase())
            .join(", ")}.`,
        ],
  };
}

/*Other helpers*/

function isPdfFile(f) {
  const ct = safeStr(f?.contentType).toLowerCase();
  if (ct.includes("pdf")) return true;
  const name = safeStr(f?.originalName).toLowerCase();
  return name.endsWith(".pdf");
}

function isImageFile(f) {
  const ct = safeStr(f?.contentType).toLowerCase();
  if (ct.startsWith("image/")) return true;
  const name = safeStr(f?.originalName).toLowerCase();
  return (
    name.endsWith(".png") ||
    name.endsWith(".jpg") ||
    name.endsWith(".jpeg") ||
    name.endsWith(".webp") ||
    name.endsWith(".gif")
  );
}

function fileTypeLabel(f) {
  const ext = extensionFromFile(f);
  if (ext && ext !== "file") return ext.toUpperCase();

  const ct = safeStr(f?.contentType);
  const slashParts = ct.split("/");
  if (slashParts.length > 1 && safeStr(slashParts[1])) {
    return safeStr(slashParts[1]).split(";")[0].toUpperCase();
  }

  return "FILE";
}

function fileSizeLabel(bytes) {
  const size = Number(bytes) || 0;
  if (size >= 1024 * 1024) {
    const mb = size / (1024 * 1024);
    return `${mb >= 10 ? Math.round(mb) : mb.toFixed(1)} MB`;
  }
  return `${Math.round(size / 1024)} KB`;
}

function StaffPdfPreview({ file, docId, onReady }) {
  const [source, setSource] = useState("");
  const [error, setError] = useState("");
  const readyRef = useRef(onReady);
  readyRef.current = onReady;
  useEffect(() => {
    const controller = new AbortController();
    let objectUrl = "";
    setSource("");
    setError("");
    async function loadPdf() {
      if (file.downloadURL?.startsWith("blob:")) {
        setSource(file.downloadURL);
        return;
      }
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error("Please sign in to view this document.");
      const response = await fetch(`${MYKYC_API_BASE}/myKyc/staff/doc-upload/download?docId=${encodeURIComponent(docId)}&objectPath=${encodeURIComponent(file.objectPath)}`, {
        headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
      });
      if (!response.ok) throw new Error("Unable to load this document. Please try again.");
      const body = await response.blob();
      if (controller.signal.aborted) return;
      objectUrl = URL.createObjectURL(new Blob([body], { type: "application/pdf" }));
      setSource(objectUrl);
    }
    loadPdf().catch(error => {
      if (controller.signal.aborted) return;
      setError(error.message);
      readyRef.current?.();
    });
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [docId, file.objectPath, file.downloadURL]);
  if (error) return <div role="alert" className="pdfDocumentPreviewStatus">{error}</div>;
  return source ? <PdfDocumentPreview file={source} onReady={onReady} /> : null;
}

function fileInfoLabel(f) {
  return `${fileTypeLabel(f)} • ${fileSizeLabel(f?.sizeBytes)}`;
}

/**
 * Extract cardId from storage objectPath:
 *   <uploadPrefix>/<cardId>/<timestamp>_<filename>
 */
function getCardIdFromObjectPath(objectPath, uploadPrefix) {
  const p = safeStr(objectPath);
  if (!p) return "";

  const parts = p.split("/").filter(Boolean);
  const prefix = safeStr(uploadPrefix).replace(/\/+$/g, "");

  if (prefix) {
    const prefParts = prefix.split("/").filter(Boolean);
    for (let i = 0; i <= parts.length - prefParts.length; i++) {
      let ok = true;
      for (let j = 0; j < prefParts.length; j++) {
        if (parts[i + j] !== prefParts[j]) {
          ok = false;
          break;
        }
      }
      if (ok) return parts[i + prefParts.length] || "";
    }
  }

  if (parts.length >= 2) return parts[parts.length - 2];
  return "";
}

/** Parse your backend "• " lines for nicer rendering */
function parseRichLines(desc) {
  const raw = String(desc || "").trim();
  if (!raw) return [];

  const out = [];
  const lines = raw
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean);

  for (const line of lines) {
    const noBullet = line.startsWith("•") ? line.slice(1).trim() : line;

    const lower = noBullet.toLowerCase();
    const isHeading =
      lower.startsWith("provide at least one of:") ||
      lower.startsWith("provide at least 1 of:") ||
      lower.startsWith("also provide (optional):");

    if (isHeading) out.push({ type: "heading", text: noBullet });
    else out.push({ type: "bullet", text: noBullet });
  }

  return out;
}

function isRequirementNoteText(text) {
  const raw = safeStr(text);
  if (!raw) return false;
  const lower = raw.toLowerCase();
  return (
    /^\(.+\)$/s.test(raw) &&
    (lower.includes("applicable documents") ||
      lower.includes("mainland company") ||
      lower.includes("free zone company"))
  );
}

function formatRequirementNoteText(text) {
  const raw = safeStr(text).replace(/^\((.*)\)$/s, "$1").trim();
  return raw ? `Note: ${raw}` : "";
}

function splitRequirementLinesForDisplay(lines) {
  const displayLines = [];
  const notes = [];

  (Array.isArray(lines) ? lines : []).forEach((line) => {
    if (line?.type === "bullet" && isRequirementNoteText(line?.text)) {
      const note = formatRequirementNoteText(line.text);
      if (note) notes.push(note);
      return;
    }
    displayLines.push(line);
  });

  return { displayLines, notes };
}

/** Build a nice subtitle from snapshot meta */
function buildSubtitleFromMeta(meta) {
  if (!meta || typeof meta !== "object") return "";

  const kind = safeStr(meta.kind); // "individual" | "entity" | "sow"
  const partyRole = safeStr(meta.partyRole);

  // individual
  const nat = safeStr(meta.nationalityIso2);
  const dom = safeStr(meta.domicileIso2);
  const dob = formatReviewDob(meta.dateOfBirth);

  // entity
  const inc = safeStr(meta.incorporationIso2);

  // sow
  const sowKey = safeStr(meta.sowKey);

  if (kind === "individual") {
    const bits = [];
    if (partyRole) bits.push(partyRole);
    const geo = [
      dob ? `DOB: ${dob}` : "",
      nat ? `Nat: ${nat}` : "",
      dom ? `Dom: ${dom}` : "",
    ]
      .filter(Boolean)
      .join(" • ");
    if (geo) bits.push(geo);
    return bits.join(" • ");
  }

  if (kind === "entity") {
    const bits = [];
    if (partyRole) bits.push(partyRole);
    if (inc) bits.push(`Country: ${inc}`);
    return bits.join(" • ");
  }

  if (kind === "sow") {
    return sowKey ? toTitleCaseLabel(SOW_LABEL_BY_VALUE[sowKey] || sowKey) : "Source of Wealth";
  }

  return "";
}

function sectionLabelFromGroup(g) {
  const sec = safeStr(g?.section);
  if (sec) return sec;

  const kind = safeStr(g?.meta?.kind);
  if (kind === "sow") return "Source of Wealth corroboration";
  return "Identification documents";
}

/* -------------------- Modal -------------------- */

function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  floatingActions,
  className = "",
  style,
  shellHeight,
  embedded = false,
  animation = "slide",
  headerIcon = null,
}) {
  const stackMotion =
    animation === "zoom"
      ? {
          initial: { opacity: 0, scale: 0.82 },
          animate: { opacity: 1, scale: 1 },
          exit: { opacity: 0, scale: 0.9 },
          transition: { duration: 0.24, ease: [0.16, 1, 0.3, 1] },
        }
      : {
          initial: { opacity: 0, y: 10, scale: 0.98 },
          animate: { opacity: 1, y: 0, scale: 1 },
          exit: { opacity: 0, y: 10, scale: 0.98 },
          transition: { duration: 0.18 },
        };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className={`docModalOverlay ${className}`}
          style={{ position: embedded ? "absolute" : "fixed", ...(embedded ? { backdropFilter: "none", WebkitBackdropFilter: "none" } : {}), ...style }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={onClose}
        >
          <motion.div
            className="docModalStack"
            initial={stackMotion.initial}
            animate={stackMotion.animate}
            exit={stackMotion.exit}
            transition={stackMotion.transition}
          >
            <motion.div
              className="docModalShell"
              onMouseDown={(e) => e.stopPropagation()}
              animate={shellHeight ? { height: shellHeight } : undefined}
              transition={
                shellHeight
                  ? {
                      height: {
                        duration: 0.8,
                        ease: [0.16, 1, 0.3, 1],
                      },
                    }
                  : undefined
              }
            >
              <div className="docModalHeader">
                {headerIcon ? (
                  <div className="docModalHeaderIcon" aria-hidden="true">
                    {headerIcon}
                  </div>
                ) : null}
                <div className="docModalTitle">{title}</div>
                <button
                  type="button"
                  className="docModalCloseBtn"
                  onClick={onClose}
                  aria-label="Close"
                  title="Close"
                >
                  <i className="bi bi-x-lg" aria-hidden="true" />
                </button>
              </div>

              <div className="docModalBody">{children}</div>
              {floatingActions ? (
                <div className="docModalFloatingActions" onMouseDown={(e) => e.stopPropagation()}>
                  {floatingActions}
                </div>
              ) : null}
            </motion.div>
            {footer ? <div className="docModalFooter" onMouseDown={(e) => e.stopPropagation()}>{footer}</div> : null}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* -------------------- Download via API -------------------- */

function filenamePart(value, fallback = "") {
  const cleaned = safeStr(value || fallback)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
  return cleaned || safeStr(fallback).toLowerCase() || "document";
}

function requirementFilenamePart(value, fallback = "kyc-document") {
  return safeStr(value) === "ID" ? "ID" : filenamePart(value, fallback);
}

function extensionFromFile(file) {
  const name = safeStr(file?.originalName || file?.name);
  const match = name.match(/\.([a-zA-Z0-9]{1,8})$/);
  if (match) return match[1].toLowerCase();

  const contentType = safeStr(file?.contentType).toLowerCase();
  if (contentType.includes("pdf")) return "pdf";
  if (contentType.includes("png")) return "png";
  if (contentType.includes("webp")) return "webp";
  if (contentType.includes("jpeg") || contentType.includes("jpg")) return "jpg";
  return "file";
}

function downloadLabelForFile({ file, group }) {
  const assignedLabel = reviewLabelFromFile(file);
  if (fileLooksLikeSowRequirement(file)) return "source-of-wealth";
  if (groupLooksLikeIdDocument(group, file)) return "ID";
  if (groupLooksLikeUtilityDocument(group, file)) return "proof-of-address";
  if (groupLooksLikeCorporateDocument(group, file)) return "corporate-document";
  if (assignedLabel) return assignedLabel;
  return group?.title || file?.cardTitle || file?.cardId || "kyc-document";
}

function titleSubjectFromGroup(group = null) {
  const title = stripMetaInTitle(group?.title || "");
  if (!title) return "";
  return title
    .replace(/\s*\([^)]*\)\s*/g, " ")
    .replace(/\b(Nationality|Country|DOB|Dom|Nat)\s*:\s*.+$/i, "")
    .replace(/\s+/g, " ")
    .trim();
}

function fileLooksLikeSowRequirement(file) {
  const key = safeStr(file?.review?.requirementKey || file?.requirementKey).toLowerCase();
  const label = normalizedRequirementText(
    [
      file?.review?.requirementText,
      file?.requirementText,
      file?.cardTitle,
      file?.cardId,
    ]
      .filter(Boolean)
      .join(" ")
  );
  return key.startsWith("sow:") || key.startsWith("sow|") || label.includes("source of wealth");
}

function fileLooksLikePartyDocument(file, group) {
  const key = safeStr(file?.review?.requirementKey || file?.requirementKey).toLowerCase();
  const meta = group?.meta || file?.reviewMeta || null;
  return (
    key.startsWith("party:") ||
    key.startsWith("party|") ||
    safeStr(meta?.partyName || meta?.name || file?.partyName)
  );
}

function downloadSubjectForFile({ file, group, sub }) {
  if (
    !fileLooksLikeSowRequirement(file) &&
    (groupLooksLikeIdDocument(group, file) ||
      groupLooksLikeCorporateDocument(group, file) ||
      fileLooksLikePartyDocument(file, group))
  ) {
    return firstNonEmpty(
      group?.meta?.partyName,
      group?.meta?.name,
      file?.partyName,
      file?.reviewMeta?.partyName,
      titleSubjectFromGroup(group)
    );
  }

  return firstNonEmpty(
    sub?.clientName,
    sub?.name,
    sub?.fullName,
    sub?.clientId,
    sub?.id
  );
}

function countryNameForPreviewTitle(value) {
  const raw = safeStr(value);
  if (!raw) return "";
  const upper = raw.toUpperCase();
  const iso2 = /^[A-Z]{2}$/.test(upper)
    ? upper
    : /^[A-Z]{3}$/.test(upper)
    ? countries.alpha3ToAlpha2(upper)
    : countries.getAlpha2Code(raw, "en");
  return (iso2 && countries.getName(iso2, "en")) || raw;
}

function countryFlagCodeForPreviewTitle(value) {
  const raw = safeStr(value);
  if (!raw) return "";
  const upper = raw.toUpperCase();
  const iso2 = /^[A-Z]{2}$/.test(upper)
    ? upper
    : /^[A-Z]{3}$/.test(upper)
    ? countries.alpha3ToAlpha2(upper)
    : countries.getAlpha2Code(raw, "en");
  return safeStr(iso2).toLowerCase();
}

function formatPreviewDob(value) {
  return formatReviewDob(value).replace(/\b[A-Z][a-z]{2}\b/, (month) => month.toLowerCase());
}

function previewModalTitleForFile({ file, group, kyc, sub }) {
  const reviewMeta = group?.meta || file?.reviewMeta || null;
  const profile = currentKycProfileForDocCheck(kyc || {}, sub || {}, reviewMeta);
  const name = firstNonEmpty(
    reviewMeta?.partyName,
    group?.meta?.partyName,
    file?.partyName,
    file?.reviewMeta?.partyName,
    profile?.fullName,
    titleSubjectFromGroup(group)
  );
  let kind = detectedDocumentKindForRequirementSelection(file);
  if (!kind) {
    const option = group?.options?.length === 1 ? group.options[0] : null;
    const key = file?.review?.requirementKey || file?.requirementKey || option?.key || "";
    const label = file?.review?.requirementText || file?.requirementText || option?.label || "";
    if (requirementSelectionLooksLikeUtilityDocument(key, label)) kind = "utility";
    else if (requirementSelectionLooksLikeIdDocument(key, label)) kind = "id";
    else if (requirementSelectionLooksLikeCorporateDocument(key, label)) kind = "corporate";
  }
  const isCustomer = !reviewMeta?.partyId || reviewMeta.partyId === "customer";
  const customer = isCustomer ? kyc?.customer || {} : {};
  const idData = file?.documentDataConsistencyCheck?.extractedData || {};
  const poaData = file?.proofOfAddressCheck?.extracted || {};
  const corporateData = file?.corporateDocumentCheck?.extracted || {};
  const expectedCorporate = file?.corporateDocumentCheck?.expectedParty || {};
  const address = firstNonEmpty(
    reviewMeta?.registeredAddress, reviewMeta?.partyAddress, file?.reviewMeta?.registeredAddress,
    customer.address,
    kind === "corporate" ? corporateData.registered_address : poaData.service_address || poaData.address || poaData.full_address || poaData.client_address
  );
  const countryItem = (label, raw, icon) => {
    const value = countryNameForPreviewTitle(raw);
    return value ? { label, value, flagCode: countryFlagCodeForPreviewTitle(raw), icon } : null;
  };
  const dateItem = (label, raw) => {
    const value = formatPreviewDob(raw);
    return value ? { label, value, icon: iconBirthday } : null;
  };
  const addressItem = address ? { label: "Address", value: address, icon: iconAddress } : null;
  const meta = (kind === "id" ? [
    countryItem("Nationality", reviewMeta?.nationalityIso2 || profile?.nationality || idData.nationality, iconPassport),
    dateItem("Date of birth", reviewMeta?.dateOfBirth || profile?.dateOfBirth || idData.dateOfBirth),
  ] : kind === "utility" ? [
    countryItem("Domicile country", reviewMeta?.domicileIso2 || file?.reviewMeta?.domicileIso2 || customer.country || poaData.address_country_iso2 || poaData.country_iso2 || poaData.country, iconAddress),
    addressItem,
  ] : kind === "corporate" ? [
    countryItem("Country of incorporation", reviewMeta?.incorporationIso2 || expectedCorporate.incorporationIso2 || customer.nationalityOrIncorporationCountry || corporateData.jurisdiction_country_iso2 || corporateData.jurisdiction_country, iconCorporateDocBlue),
    addressItem,
    dateItem("Date of incorporation", reviewMeta?.incorporationDate || reviewMeta?.dobOrIncorporationDate || reviewMeta?.dateOfBirth || expectedCorporate.incorporationDate || customer.dobOrIncorporationDate || corporateData.incorporation_date),
  ] : []).filter(Boolean);

  if (!name && !meta.length) return safeStr(file?.originalName) || "Preview";

  return (
    <span className="docPreviewTitleMeta">
      {name ? <span className="docPreviewTitleName">{name}</span> : null}
      <span className="docPreviewTitleBadges">
      {meta.map((item) => (
        <span className={`docPreviewTitleItem${item.label === "Address" ? " docPreviewTitleItemAddress" : ""}`} key={item.label} title={`${item.label}: ${item.value}`} aria-label={`${item.label}: ${item.value}`}>
          {item.icon ? (
            <img
              src={item.icon}
              alt=""
              className="docPreviewTitleIcon"
              aria-hidden="true"
            />
          ) : null}
          <span className="docPreviewTitleValue">
            {item.flagCode ? (
              <CircleFlag
                countryCode={item.flagCode}
                className="docPreviewTitleFlag"
                aria-hidden="true"
              />
            ) : null}
            <span>{item.value}</span>
          </span>
        </span>
      ))}
      </span>
    </span>
  );
}

function logicalDownloadLabelForPeer({ file, currentGroup, currentFile }) {
  const assignedLabel = reviewLabelFromFile(file);
  const label = downloadLabelForFile({ file, group: currentGroup });
  if (
    label === "source-of-wealth" ||
    label === "ID" ||
    label === "proof-of-address" ||
    label === "corporate-document"
  ) {
    return label;
  }
  if (assignedLabel) return assignedLabel;

  const currentCardId = safeStr(currentFile?.cardId || currentGroup?.cardId);
  const fileCardId = safeStr(file?.cardId);
  if (currentCardId && fileCardId === currentCardId) {
    return downloadLabelForFile({ file, group: currentGroup });
  }

  return file?.cardTitle || file?.cardId || "kyc-document";
}

function duplicateDownloadSuffix({ file, group, sub, requirement, ext }) {
  const files = Array.isArray(sub?.files) ? sub.files : [];
  const peers = files.filter((candidate) => {
    const candidateRequirement = requirementFilenamePart(
      logicalDownloadLabelForPeer({
        file: candidate,
        currentGroup: group,
        currentFile: file,
      }),
      "kyc-document"
    );
    return candidateRequirement === requirement && extensionFromFile(candidate) === ext;
  });

  if (peers.length <= 1) return "";

  const currentPath = safeStr(file?.objectPath);
  const index = peers.findIndex((candidate) => safeStr(candidate?.objectPath) === currentPath);
  const n = index >= 0 ? index : peers.length - 1;
  let value = "";
  let cursor = n;
  do {
    value = String.fromCharCode(65 + (cursor % 26)) + value;
    cursor = Math.floor(cursor / 26) - 1;
  } while (cursor >= 0);
  return `_${value}`;
}

function logicalDownloadFilename({ file, group, sub }) {
  const client = filenamePart(
    downloadSubjectForFile({ file, group, sub }),
    "client"
  );
  const requirementLabel = downloadLabelForFile({ file, group });
  const requirement = requirementFilenamePart(requirementLabel, "kyc-document");
  const date = new Date().toISOString().slice(0, 10);
  const ext = extensionFromFile(file);
  const suffix = duplicateDownloadSuffix({ file, group, sub, requirement, ext });
  const base = [client, requirement, date]
    .filter(Boolean)
    .join("_")
    .slice(0, 160)
    .replace(/_+$/g, "");
  return `${base}${suffix}.${ext}`;
}

async function downloadViaApi({ docId, file, group = null, sub = null }) {
  const objectPath = safeStr(file?.objectPath);
  const filename = logicalDownloadFilename({ file, group, sub });
  if (!docId || !objectPath) return;

  const user = auth.currentUser;
  if (!user) {
    alert("Not signed in");
    return;
  }

  const token = await user.getIdToken();

  const url =
    `${MYKYC_API_BASE}/myKyc/staff/doc-upload/download` +
    `?docId=${encodeURIComponent(docId)}` +
    `&objectPath=${encodeURIComponent(objectPath)}` +
    `&filename=${encodeURIComponent(filename)}`;

  const res = await fetch(url, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(txt || `Download failed (${res.status})`);
  }

  const blob = await res.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(blobUrl);
}

// -------------------------------------------------
// -------------------- Page --------------------
// -------------------------------------------------

export default function StaffDocumentsReviewPage({ docId: docIdProp = null, onClose = null, isModal = false, onInnerModalChange = null, initialObjectPath = "", documentOnly = false }) {
  const nav = useNavigate();

  const { docId: docIdParam } = useParams();
  const { state } = useLocation();
  const docId = safeStr(docIdProp || docIdParam || state?.clientId || "");

  const { showOverlay, hideOverlay } = useAppLoading();
  const { authLoading, isAdmin, me, role } = useAuth();
  // Staff front may view every document and check result, but never decide.
  const canDecide = canDecideDocumentReview(role);
  const myOrgId = String(me?.orgId || "").trim();
  const myTeamId = String(me?.teamId || "").trim();

  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState(null);
  const [kycLive, setKycLive] = useState(null);
  const [documentDataConsistencyScanEnabled, setDocumentDataConsistencyScanEnabled] = useState(false);
  const [err, setErr] = useState("");
  const submissionSnapshotSeq = useRef(0);
  const directOpenAttempted = useRef(false);
  const [directReviewOpened, setDirectReviewOpened] = useState(false);
  const directReviewLoading = documentOnly && (loading || (!directReviewOpened && !err));

  // Keep the shared global overlay alive while this page is loading.
  // useLayoutEffect runs children-before-parents so our showOverlay fires
  // before ProtectedRoute's cleanup can decrement the count to zero.
  useLayoutEffect(() => {
    if (isModal || !loading) return;
    showOverlay({ title: "Loading…", subtitle: "", loaderStyle: "dots" });
    return () => hideOverlay();
  }, [isModal, loading, showOverlay, hideOverlay]);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewGroup, setPreviewGroup] = useState(null);
  const [previewImageMode, setPreviewImageMode] = useState("full");
  const [previewContentLoaded, setPreviewContentLoaded] = useState(false);
  const [rerunningDocCheckPath, setRerunningDocCheckPath] = useState("");
  const [previewModalMetrics, setPreviewModalMetrics] = useState(() => {
    const height = typeof window === "undefined" ? 900 : window.innerHeight;
    const expanded = Math.min(Math.round(height * 0.76), 720);
    return { expanded, collapsed: Math.min(Math.round(height * 0.6), 560), stack: Math.min(expanded + 54, height - 32) };
  });

  // -------------------- Per-file approval modal --------------------
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignFile, setAssignFile] = useState(null); // the uploaded file object
  const [assignGroup, setAssignGroup] = useState(null); // the requirement card group (g)
  const [assignOptions, setAssignOptions] = useState([]);
  const [assignSuggestedRequirementKey, setAssignSuggestedRequirementKey] = useState("");
  const [assignKycUpdates, setAssignKycUpdates] = useState({});
  const [kycUpdateConfirmOpen, setKycUpdateConfirmOpen] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(null);
  const kycUpdateContinueBtnRef = useRef(null);
  const kycUpdateYesBtnRef = useRef(null);
  const kycUpdateNoBtnRef = useRef(null);
  const [docCheckDetail, setDocCheckDetail] = useState(null);
  const [assignPanelReady, setAssignPanelReady] = useState(false); // [{key,label}] preferred

  // -------------------- Per-file reject modal --------------------
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectFile, setRejectFile] = useState(null);
  const [rejectGroup, setRejectGroup] = useState(null);
  const [rejectOptions, setRejectOptions] = useState([]);
  const [rejectSuggestedRequirementKey, setRejectSuggestedRequirementKey] = useState("");
  const [rejectPanelReady, setRejectPanelReady] = useState(false);

  useEffect(() => {
    if (!previewOpen && !documentOnly) return undefined;

    function updatePreviewModalMetrics() {
      const viewportHeight =
        typeof window === "undefined" ? 900 : window.innerHeight;
      const expanded = Math.min(Math.round(viewportHeight * 0.76), 720);
      const collapsed = Math.min(Math.round(viewportHeight * 0.6), 560);
      const stack = Math.min(expanded + 54, viewportHeight - 32);

      setPreviewModalMetrics({ expanded, collapsed, stack });
    }

    updatePreviewModalMetrics();
    window.addEventListener("resize", updatePreviewModalMetrics);
    return () => window.removeEventListener("resize", updatePreviewModalMetrics);
  }, [previewOpen, documentOnly]);

  // Reset loader whenever the previewed file changes
  useEffect(() => {
    setPreviewContentLoaded(false);
  }, [previewFile?.objectPath]);

  function openPreviewFile(file, group) {
    if (!file?.downloadURL) return;
    setPreviewFile(file);
    setPreviewGroup(group);
    setPreviewImageMode("full");
    setPreviewOpen(true);
  }

  function handlePreviewCardKeyDown(event, file, group) {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    openPreviewFile(file, group);
  }

  async function rerunDocumentDataConsistencyCheck(file, group = null) {
    if (!canDecide) return; // writes check results into the submission
    const objectPath = safeStr(file?.objectPath);
    if (!sub?.id || !objectPath) return;
    const isCorporateContext = groupLooksLikeCorporateDocument(group, file);
    const user = auth.currentUser;
    if (!user) {
      alert("Not signed in");
      return;
    }

    const startedAt = Date.now();
    const minSpinnerMs = 800;
    setRerunningDocCheckPath(objectPath);
    try {
      await new Promise((resolve) => requestAnimationFrame(resolve));
      const token = await user.getIdToken();
      const res = await fetch(
        `${MYKYC_API_BASE}/myKyc/review-support/document-data-consistency/mrz-scan`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            docId: sub.id,
            objectPath,
            scanIntent: isCorporateContext ? "corporate_document" : "",
          }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) {
        throw new Error(data?.error || `Document check failed (${res.status})`);
      }
      const returnedResult = data?.result || null;
      const returnedScanType = safeStr(returnedResult?.scanType);
      const scanPatch = { review: null };
      if (isCorporateContext) {
        scanPatch.corporateDocumentCheck = returnedResult;
        scanPatch.documentDataConsistencyCheck = null;
        scanPatch.proofOfAddressCheck = null;
        scanPatch.documentClassificationCheck = null;
      } else if (returnedScanType === "passport_mrz") {
        scanPatch.documentDataConsistencyCheck = returnedResult;
      } else if (returnedScanType === "proof_of_address") {
        scanPatch.proofOfAddressCheck = returnedResult;
      } else if (returnedScanType === "corporate_document") {
        scanPatch.corporateDocumentCheck = returnedResult;
      } else if (returnedScanType === "classification") {
        scanPatch.documentClassificationCheck = returnedResult;
      }
      const nextFiles = patchFileInArray(sub?.files, objectPath, scanPatch);
      await updateDoc(doc(db, "documentSubmissions", sub.id), {
        files: reviewFilesForStorage(nextFiles),
        status: "pending_review",
        updatedAt: serverTimestamp(),
      });
      setSub((prev) => ({
        ...(prev || {}),
        files: patchFileInArray(prev?.files, objectPath, scanPatch),
      }));
      setPreviewFile((prev) =>
        safeStr(prev?.objectPath) === objectPath
          ? {
              ...prev,
              ...scanPatch,
            }
          : prev
      );
    } catch (e) {
      alert(e?.message || "ID check failed");
    } finally {
      const remainingSpinnerMs = Math.max(0, minSpinnerMs - (Date.now() - startedAt));
      if (remainingSpinnerMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingSpinnerMs));
      }
      setRerunningDocCheckPath("");
    }
  }

  function closeApprovalFlow() {
    setAssignOpen(false);
    setAssignFile(null);
    setAssignGroup(null);
    setAssignOptions([]);
    setAssignSuggestedRequirementKey("");
    setAssignKycUpdates({});
    setKycUpdateConfirmOpen(false);
    setPendingApproval(null);
    setPreviewOpen(false);
    setPreviewFile(null);
    setPreviewGroup(null);
    setPreviewImageMode("full");
  }

  // Lock body scroll when any modal is open
  useEffect(() => {
    const anyOpen = documentOnly || previewOpen || assignOpen || rejectOpen || kycUpdateConfirmOpen || !!docCheckDetail;
    if (!anyOpen) return;
    const body = document.body;
    const html = document.documentElement;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflowY: body.style.overflowY,
      paddingRight: body.style.paddingRight,
    };
    const scrollbarW = window.innerWidth - html.clientWidth;
    if (scrollbarW > 0) body.style.paddingRight = `${scrollbarW}px`;
    html.style.overflow = "hidden";
    body.style.overflowY = "hidden";
    return () => {
      html.style.overflow = prev.htmlOverflow;
      body.style.overflowY = prev.bodyOverflowY;
      body.style.paddingRight = prev.paddingRight;
    };
  }, [documentOnly, previewOpen, assignOpen, rejectOpen, kycUpdateConfirmOpen, docCheckDetail]);

  // Notify parent when any inner modal opens/closes (used to blur header)
  useEffect(() => {
    if (!onInnerModalChange) return;
    onInnerModalChange(previewOpen || assignOpen || rejectOpen || kycUpdateConfirmOpen || !!docCheckDetail);
  }, [previewOpen, assignOpen, rejectOpen, kycUpdateConfirmOpen, docCheckDetail, onInnerModalChange]);

  // Delay the requirement panel until the shell-shrink animation finishes (1 s)
  useEffect(() => {
    if (!assignOpen) {
      setAssignPanelReady(false);
      setAssignSuggestedRequirementKey("");
      setAssignKycUpdates({});
      return undefined;
    }
    const t = setTimeout(() => setAssignPanelReady(true), 500);
    return () => clearTimeout(t);
  }, [assignOpen]);

  useEffect(() => {
    if (!assignPanelReady || !assignSuggestedRequirementKey) return undefined;
    const t = setTimeout(() => {
      document
        .querySelector('[data-requirement-suggested="assign"]')
        ?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [assignPanelReady, assignSuggestedRequirementKey]);

  useEffect(() => {
    if (!rejectOpen) {
      setRejectPanelReady(false);
      setRejectSuggestedRequirementKey("");
      return undefined;
    }
    const t = setTimeout(() => setRejectPanelReady(true), 500);
    return () => clearTimeout(t);
  }, [rejectOpen]);

  useEffect(() => {
    if (!rejectPanelReady || !rejectSuggestedRequirementKey) return undefined;
    const t = setTimeout(() => {
      document
        .querySelector('[data-requirement-suggested="reject"]')
        ?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [rejectPanelReady, rejectSuggestedRequirementKey]);

  function getRequirementOptionsForGroup(g) {
    const opts = Array.isArray(g?.options) ? g.options : [];
    if (opts.length) return opts; // [{key,label}]

    // fallback (old behavior)
    const lines = parseRichLines(g?.desc);
    const bullets = lines
      .filter((x) => x.type === "bullet")
      .filter((x) => !isRequirementNoteText(x.text))
      .map((x) => ({ key: safeStr(x.text), label: safeStr(x.text) }))
      .filter((x) => x.key && x.label);

    if (bullets.length) return bullets;

    const cleanTitle = stripMetaInTitle(g?.title || "");
    return cleanTitle ? [{ key: cleanTitle, label: cleanTitle }] : [];
  }

  function kycUpdateOptionsForApprovalSelection(file, group, requirementKey, requirementText) {
    if (file?.corporateDocumentCheck) {
      return corporateDataUpdateOptionsFromFile(
        file,
        kycLive || {},
        sub || {},
        group?.meta || null
      );
    }

    if (requirementSelectionLooksLikeIdDocument(requirementKey, requirementText)) {
      return idDataUpdateOptionsFromFile(
        file,
        kycLive || {},
        sub || {},
        group?.meta || null
      );
    }

    if (requirementSelectionLooksLikeUtilityDocument(requirementKey, requirementText)) {
      return proofOfAddressUpdateOptionsFromFile(
        file,
        kycLive || {},
        sub || {},
        group?.meta || null
      );
    }

    return [];
  }

  function selectedKycUpdatesForOptions(options) {
    return (Array.isArray(options) ? options : []).reduce((acc, option) => {
      acc[option.key] = /^add\b/i.test(safeStr(option?.label));
      return acc;
    }, {});
  }

  function patchFileInArray(filesArr, objectPath, patch) {
    const p = safeStr(objectPath);
    return (Array.isArray(filesArr) ? filesArr : []).map((x) => {
      if (safeStr(x?.objectPath) !== p) return x;
      return { ...x, ...patch };
    });
  }

  function isReviewedStatus(s) {
    const v = safeStr(s);
    return v === "approved" || v === "rejected";
  }

  async function updateSubmissionStatusIfFinished(updatedFiles) {
    // only consider real files
    const arr = (Array.isArray(updatedFiles) ? updatedFiles : []).filter(
      (f) => safeStr(f?.objectPath) // ignore any weird empty entries
    );

    const allReviewed =
      arr.length > 0 && arr.every((f) => !requiresDocumentReview(f) || isReviewedStatus(f?.review?.status));

    await updateDoc(doc(db, "documentSubmissions", sub.id), {
      status: allReviewed ? "reviewed" : "pending_review",
      updatedAt: serverTimestamp(),
      ...(allReviewed ? { reviewedAt: serverTimestamp() } : {}),
    });

    if (allReviewed) { if (onClose) onClose(); else nav("/kyc-inbox"); }
  }

  async function approveSingleFile(...guardedArgs) {
    if (!canDecide) return;
    return approveSingleFileInner(...guardedArgs);
  }
  async function approveSingleFileInner({
    file,
    group,
    requirementKey,
    requirementText,
    kycProfileUpdateOptions = [],
    selectedKycUpdates = {},
  }) {
    if (!sub?.id) return;

    const reviewedBy = auth?.currentUser?.uid || null;

    const review = {
      status: "approved",
      cardId: safeStr(group?.cardId),

      // ✅ stable selection (Option 2)
      requirementKey: safeStr(requirementKey),
      requirementText: safeStr(requirementText),

      reviewedBy,
      // reviewedByUid: safeStr(user?.uid),
      // reviewedByName: safeStr(user?.displayName),
      // reviewedByEmail: safeStr(user?.email),

      reviewedAt: new Date().toISOString(),
    };

    const updatedFiles = patchFileInArray(sub?.files, file?.objectPath, {
      review,
    });
    const updatedFile = updatedFiles.find(
      (f) => safeStr(f?.objectPath) === safeStr(file?.objectPath)
    );
    if (updatedFile) {
      setPreviewFile((prev) =>
        safeStr(prev?.objectPath) === safeStr(file?.objectPath)
          ? { ...prev, ...updatedFile }
          : prev
      );
    }

    await updateDoc(doc(db, "documentSubmissions", sub.id), {
      files: reviewFilesForStorage(updatedFiles),
      updatedAt: serverTimestamp(),
    });

    await updateSubmissionStatusIfFinished(updatedFiles);

    await applyRequirementToKycChecklist({
      clientId: safeStr(sub?.clientId), // IMPORTANT: must exist in documentSubmissions
      requirementKey,
      idExpiryDate: file?.documentDataConsistencyCheck?.extractedData?.expiryDate || "",
    });

    await updateKycProfileFromDocumentData({
      clientId: safeStr(sub?.clientId),
      options: kycProfileUpdateOptions,
      selected: selectedKycUpdates,
      kyc: kycLive || {},
    });

    await load();
  }

  async function approvePendingSelection({ applyKycUpdates }) {
    if (!canDecide) return;
    if (!pendingApproval) return;
    const selectedRequirementCanUpdateKyc = !!pendingApproval.kycProfileUpdateOptions?.length;
    await approveSingleFile({
      ...pendingApproval,
      kycProfileUpdateOptions: applyKycUpdates && selectedRequirementCanUpdateKyc
        ? pendingApproval.kycProfileUpdateOptions
        : [],
      selectedKycUpdates: applyKycUpdates && selectedRequirementCanUpdateKyc
        ? assignKycUpdates
        : {},
    });
    closeApprovalFlow();
  }

  async function rejectSingleFileOLD({ file, group }) {
    if (!canDecide) return;
    if (!sub?.id) return;

    const user = auth?.currentUser || null;
    const reviewedByUid = safeStr(user?.uid);
    const reviewedByName = safeStr(user?.displayName); // you can store but show only if you want

    // ✅ the only correct key to uncheck is what this file was previously approved as
    const prevKey = safeStr(file?.review?.requirementKey);

    const review = {
      status: "rejected",
      cardId: safeStr(group?.cardId),

      // keep trace of what it was counted for (important!)
      requirementKey: prevKey,
      requirementText: safeStr(file?.review?.requirementText),

      reviewedBy: reviewedByUid,
      reviewedByName, // optional
      reviewedAt: new Date().toISOString(),
    };

    const updatedFiles = patchFileInArray(sub?.files, file?.objectPath, {
      review,
    });
    const updatedFile = updatedFiles.find(
      (f) => safeStr(f?.objectPath) === safeStr(file?.objectPath)
    );
    if (updatedFile) {
      setPreviewFile((prev) =>
        safeStr(prev?.objectPath) === safeStr(file?.objectPath)
          ? { ...prev, ...updatedFile }
          : prev
      );
    }

    await updateDoc(doc(db, "documentSubmissions", sub.id), {
      files: reviewFilesForStorage(updatedFiles),
      updatedAt: serverTimestamp(),
    });

    // ✅ uncheck only if there was a previous approval mapping
    if (prevKey) {
      await uncheckRequirementInKycChecklist({
        clientId: safeStr(sub?.clientId),
        requirementKey: prevKey,
      });
    }

    await load();
  }

  async function onApproveClick(file, group) {
    if (!canDecide) return;
    const rawOptions = getRequirementOptionsForGroup(group); // [{key,label}]
    const fallbackOptions = [
      {
        key: `legacy:${stripMetaInTitle(group?.title || "Requirement")}`,
        label: stripMetaInTitle(group?.title || "Requirement"),
      },
    ];
    const { options, suggestedKey } = sortRequirementOptionsForDetectedDocument(
      file,
      rawOptions.length ? rawOptions : fallbackOptions
    );
    const defaultKycUpdateOptions = [
      ...idDataUpdateOptionsFromFile(file, kycLive || {}, sub || {}, group?.meta || null),
      ...corporateDataUpdateOptionsFromFile(file, kycLive || {}, sub || {}, group?.meta || null),
    ];
    const selectedKycUpdates = selectedKycUpdatesForOptions(defaultKycUpdateOptions);

    setRejectOpen(false);
    setRejectFile(null);
    setRejectGroup(null);
    setRejectOptions([]);
    setRejectSuggestedRequirementKey("");

    setAssignFile(file);
    setAssignGroup(group);
    setAssignKycUpdates(selectedKycUpdates);

    if (options.length === 1) {
      const onlyOption = options[0];
      const key = safeStr(onlyOption?.key) || safeStr(onlyOption);
      const label = safeStr(onlyOption?.label) || safeStr(onlyOption);
      const kycUpdateOptions = kycUpdateOptionsForApprovalSelection(file, group, key, label);
      const selectedUpdates = selectedKycUpdatesForOptions(kycUpdateOptions);
      const approval = {
        file,
        group,
        requirementKey: key,
        requirementText: label,
        kycProfileUpdateOptions: kycUpdateOptions,
        selectedKycUpdates: selectedUpdates,
      };
      setAssignKycUpdates(selectedUpdates);

      setAssignOptions([]);
      setAssignSuggestedRequirementKey("");

      if (approval.kycProfileUpdateOptions.length) {
        setPendingApproval(approval);
        setKycUpdateConfirmOpen(true);
        return;
      }

      try {
        await approveSingleFile(approval);
        closeApprovalFlow();
      } catch (e) {
        alert(e?.message || "Approve failed");
      }
      return;
    }

    setAssignOptions(options);
    setAssignSuggestedRequirementKey(suggestedKey);
    setAssignOpen(true);
  }

  async function rejectSingleFile(...guardedArgs) {
    if (!canDecide) return;
    return rejectSingleFileInner(...guardedArgs);
  }
  async function rejectSingleFileInner({
    file,
    group,
    // optional: used only when rejecting a never-approved file
    requirementKey,
    requirementText,
  }) {
    if (!sub?.id) return;

    const user = auth?.currentUser || null;

    // ✅ what this file was previously approved as (if any)
    const prevApprovedKey =
      safeStr(file?.review?.status) === "approved"
        ? safeStr(file?.review?.requirementKey)
        : "";

    // ✅ if the file was never approved, allow staff to tag what it was rejected for
    const chosenKey = safeStr(requirementKey);
    const chosenText = safeStr(requirementText);

    const review = {
      status: "rejected",
      cardId: safeStr(group?.cardId),

      requirementKey: chosenKey || prevApprovedKey,
      requirementText:
        chosenText ||
        safeStr(file?.review?.requirementText) ||
        reviewLabelFromFile(file),

      reviewedAt: new Date().toISOString(),

      // store uid if you want for audit, but you said don't display it in UI
      reviewedBy: safeStr(user?.uid),
      // reviewedByName: safeStr(user?.displayName), // optional if you later want it
    };

    const updatedFiles = patchFileInArray(sub?.files, file?.objectPath, {
      review,
    });
    const updatedFile = updatedFiles.find(
      (f) => safeStr(f?.objectPath) === safeStr(file?.objectPath)
    );
    if (updatedFile) {
      setPreviewFile((prev) =>
        safeStr(prev?.objectPath) === safeStr(file?.objectPath)
          ? { ...prev, ...updatedFile }
          : prev
      );
    }

    await updateDoc(doc(db, "documentSubmissions", sub.id), {
      files: reviewFilesForStorage(updatedFiles),
      updatedAt: serverTimestamp(),
    });

    await updateSubmissionStatusIfFinished(updatedFiles);

    // ✅ only uncheck in KYC if we are undoing a previous approval
    if (prevApprovedKey) {
      await uncheckRequirementInKycChecklist({
        clientId: safeStr(sub?.clientId),
        requirementKey: prevApprovedKey,
      });
    }

    await load();
  }

  async function onRejectClick(file, group) {
    if (!canDecide) return;
    const rawOptions = getRequirementOptionsForGroup(group); // [{key,label}]
    const fallbackOptions = [
      {
        key: `legacy:${stripMetaInTitle(group?.title || "Requirement")}`,
        label: stripMetaInTitle(group?.title || "Requirement"),
      },
    ];
    const { options, suggestedKey } = sortRequirementOptionsForDetectedDocument(
      file,
      rawOptions.length ? rawOptions : fallbackOptions
    );

    if (options.length === 1) {
      const onlyOption = options[0];
      const key = safeStr(onlyOption?.key) || safeStr(onlyOption);
      const label = safeStr(onlyOption?.label) || safeStr(onlyOption);

      setRejectOpen(false);
      setRejectFile(null);
      setRejectGroup(null);
      setRejectOptions([]);
      setRejectSuggestedRequirementKey("");

      try {
        await rejectSingleFile({
          file,
          group,
          requirementKey: key,
          requirementText: label,
        });

        setPreviewOpen(false);
        setPreviewFile(null);
        setPreviewGroup(null);
        setPreviewImageMode("full");
      } catch (e) {
        alert(e?.message || "Reject failed");
      }
      return;
    }

    setRejectFile(file);
    setRejectGroup(group);
    setRejectOptions(options);
    setRejectSuggestedRequirementKey(suggestedKey);
    setRejectOpen(true);
  }

  // end per file approval

  const reviewFileResolver = useMemo(() => createReviewFileResolver({
    storageUrl: objectPath => getDownloadURL(storageRef(storage, objectPath)),
    download: async objectPath => {
      const user = auth.currentUser;
      if (!user) throw new Error("Please sign in to review this document.");
      const token = await user.getIdToken();
      return fetch(`${MYKYC_API_BASE}/myKyc/staff/doc-upload/download?docId=${encodeURIComponent(docId)}&objectPath=${encodeURIComponent(objectPath)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
  }), [docId]);

  useEffect(() => () => reviewFileResolver.dispose(), [reviewFileResolver]);

  async function hydrateFilesWithUrls(files) {
    return Promise.all((Array.isArray(files) ? files : []).map(async file => {
      try {
        return { ...file, downloadURL: await reviewFileResolver.resolve(file), previewError: "" };
      } catch (error) {
        return { ...file, downloadURL: null, previewError: error.message };
      }
    }));
  }

  async function load() {
    setLoading(true);
    setErr("");
    try {
      if (!docId) throw new Error("Missing docId in route.");

      const snap = await getDoc(doc(db, "documentSubmissions", docId));
      if (!snap.exists()) throw new Error("No document submission found.");

      const data = snap.data() || {};
      const subOrgId = String(data.orgId || "").trim();
      if (!isAdmin && subOrgId && subOrgId !== myOrgId) {
        throw new Error("Access denied: this document submission belongs to another organisation.");
      }

      let scanEnabled = false;
      if (subOrgId) {
        try {
          const orgSnap = await getDoc(doc(db, "orgs", subOrgId));
          const org = orgSnap.exists() ? orgSnap.data() || {} : {};
          scanEnabled =
            org?.settings?.documentDataConsistencyCheck?.scanIdEnabled === true;
        } catch (_) {
          scanEnabled = false;
        }
      }

      if (!isAdmin && role !== "manager") {
        const clientId = safeStr(data.clientId);
        if (clientId) {
          const clientSnap = await getDoc(doc(db, "clients", clientId));
          const client = clientSnap.exists() ? clientSnap.data() || {} : {};
          const cAccessScope = String(client.accessScope || data.accessScope || "").trim();
          const cOwnerUid = String(client.ownerUid || data.ownerUid || "").trim();
          const cTeamId = String(client.teamId || data.teamId || "").trim();
          const myUid = String(auth.currentUser?.uid || me?.uid || "").trim();
          if (
            !(
              (cAccessScope === "team" && myTeamId && cTeamId === myTeamId) ||
              (cAccessScope === "individual" && cOwnerUid === myUid)
            )
          ) {
            throw new Error("Access denied: this document submission belongs to another staff member.");
          }
        }
      }

      const filesHydrated = await hydrateFilesWithUrls(data.files);
      const clientId = safeStr(data.clientId);
      let liveKycData = null;
      if (clientId) {
        try {
          const kycSnap = await getDoc(doc(db, "kyc", clientId));
          liveKycData = kycSnap.exists() ? kycSnap.data() || {} : null;
        } catch (_) {
          liveKycData = null;
        }
      }

      setKycLive(liveKycData);
      setDocumentDataConsistencyScanEnabled(scanEnabled);
      setSub({ id: snap.id, ...data, files: filesHydrated });
    } catch (e) {
      setKycLive(null);
      setDocumentDataConsistencyScanEnabled(false);
      setErr(e?.message || "Load failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!authLoading) load();
    // eslint-disable-next-line
  }, [docId, authLoading, isAdmin, myOrgId, myTeamId, role]);

  useEffect(() => {
    if (authLoading || !docId || sub?.id !== docId) return undefined;

    let active = true;
    const subRef = doc(db, "documentSubmissions", docId);
    const unsubscribe = onSnapshot(
      subRef,
      async (snap) => {
        if (!snap.exists()) return;
        const seq = ++submissionSnapshotSeq.current;
        const data = snap.data() || {};
        try {
          const filesHydrated = await hydrateFilesWithUrls(data.files);
          if (!active || seq !== submissionSnapshotSeq.current) return;
          setSub((prev) => ({
            ...(prev || {}),
            ...data,
            id: snap.id,
            files: filesHydrated,
          }));
        } catch (_) {
          return;
        }
      },
      () => {}
    );

    return () => {
      active = false;
      unsubscribe();
    };
  }, [authLoading, docId, sub?.id]);

  const files = useMemo(
    () => (Array.isArray(sub?.files) ? sub.files : []),
    [sub]
  );

  const cardSnapshots = useMemo(() => {
    const cs = sub?.cardSnapshots;
    return cs && typeof cs === "object" && !Array.isArray(cs) ? cs : {};
  }, [sub]);

  const livePartyMetaById = useMemo(() => {
    if (!kycLive) return {};
    const map = {};
    const checklistParties = kycLive?.documentsChecklist?.parties || {};

    Object.entries(checklistParties).forEach(([partyId, p]) => {
      const meta = partyToReviewMeta({ partyId, ...(p || {}) });
      if (meta?.partyId) map[meta.partyId] = meta;
    });

    buildLivePartiesFromKyc(kycLive || {}).forEach((p) => {
      const meta = partyToReviewMeta(p);
      if (meta?.partyId) map[meta.partyId] = { ...(map[meta.partyId] || {}), ...meta };
    });

    return map;
  }, [kycLive]);

  // Enrich files with normalized cardId + snapshot lookup
  const filesEnriched = useMemo(() => {
    const up = safeStr(sub?.uploadPrefix);
    const cs = cardSnapshots;

    return files.map((f) => {
      const cardId =
        safeStr(f?.cardId) || getCardIdFromObjectPath(f?.objectPath, up) || "";

      const snap = cardId ? cs[cardId] : null;

      return {
        ...f,
        __cardId: cardId || "",
        __snap: snap && typeof snap === "object" ? snap : null,
      };
    });
  }, [files, sub?.uploadPrefix, cardSnapshots]);

  /**
   * ✅ MAIN FIX:
   * Build groups from cardSnapshots first (so requirements always show),
   * then attach files to each group.
   * Also add "uncategorized" group for files not in snapshots.
   *
   * ✅ NEW:
   * store section + options from snapshots if present
   */
  const groupedByCard = useMemo(() => {
    const cs = cardSnapshots || {};
    const groups = new Map();

    // 1) Create groups from cardSnapshots (ALL requirements)
    Object.keys(cs).forEach((id) => {
      const cardId = safeStr(id);
      if (!cardId) return;
      const snap = cs[cardId] || {};
      if (snap.options?.length && snap.options.every(option => !requiresDocumentReview({ requirementKey: option.key }))) return;
      const liveMeta = livePartyMetaById[getPartyIdFromSnap(snap)] || null;
      let meta = mergeSnapshotMetaWithLive(snap.meta, liveMeta);
      const isCustomerIndividualCard =
        safeStr(meta?.kind) === "individual" &&
        /customer/i.test(safeStr(meta?.partyRole || snap?.title || cardId));
      if (isCustomerIndividualCard && !meta?.dateOfBirth) {
        meta = {
          ...(meta || {}),
          dateOfBirth:
            kycLive?.customer?.dobOrIncorporationDate ||
            kycLive?.customer?.dateOfBirth ||
            kycLive?.customer?.dob ||
            "",
        };
      }
      groups.set(cardId, {
        cardId,
        section: safeStr(snap.section) || "",
        title: safeStr(snap.title) || `Card ${cardId}`,
        subtitle: buildSubtitleFromMeta(meta),
        desc: safeStr(snap.description) || "",
        meta,
        options: Array.isArray(snap.options) ? snap.options : [],
        files: [],
      });
    });

    // 2) Attach files into their card group (or uncategorized)
    for (const f of filesEnriched) {
      if (!requiresDocumentReview(f)) continue;
      const cid = safeStr(f.__cardId);

      if (cid && groups.has(cid)) {
        groups.get(cid).files.push(f);
      } else {
        const key = "uncategorized";
        if (!groups.has(key)) {
          groups.set(key, {
            cardId: key,
            section: "",
            title: "Uncategorized",
            subtitle: "",
            desc: "",
            meta: null,
            options: [],
            files: [],
          });
        }
        groups.get(key).files.push(f);
      }
    }

    // 3) Order: requirements with files first, then empty requirements, uncategorized last
    const arr = Array.from(groups.values());
    arr.sort((a, b) => {
      if (a.cardId === "uncategorized") return 1;
      if (b.cardId === "uncategorized") return -1;

      const aHas = (a.files || []).length > 0;
      const bHas = (b.files || []).length > 0;
      if (aHas !== bHas) return aHas ? -1 : 1;

      return (a.title || a.cardId).localeCompare(b.title || b.cardId);
    });

    // If uncategorized has no files, drop it
    return arr.filter((g) => g.cardId !== "uncategorized" || g.files.length);
  }, [cardSnapshots, filesEnriched, livePartyMetaById, kycLive]);

  // Select the file before paint while retaining the existing loading shell.
  useLayoutEffect(() => {
    if (!documentOnly || loading || err || directOpenAttempted.current) return;
    directOpenAttempted.current = true;
    const group = groupedByCard.find(g => g.files.some(f => f.objectPath === initialObjectPath));
    const file = group?.files.find(f => f.objectPath === initialObjectPath);
    if (!file?.downloadURL) {
      setErr(file ? (file.previewError || "Unable to load this document for review. Please try again.") : "This document is no longer listed in this submission. Please refresh the checklist.");
      return;
    }
    setPreviewFile(file);
    setPreviewGroup(group);
    setPreviewImageMode("full");
    setPreviewOpen(true);
    setDirectReviewOpened(true);
  }, [documentOnly, loading, err, groupedByCard, initialObjectPath]);

  useEffect(() => {
    if (documentOnly && directReviewOpened && !previewOpen && !assignOpen && !rejectOpen && !kycUpdateConfirmOpen && !docCheckDetail) onClose?.();
  }, [documentOnly, directReviewOpened, previewOpen, assignOpen, rejectOpen, kycUpdateConfirmOpen, docCheckDetail, onClose]);

  const assignKycUpdateOptions = useMemo(
    () => [
      ...idDataUpdateOptionsFromFile(
        assignFile,
        kycLive || {},
        sub || {},
        assignGroup?.meta || null
      ),
      ...proofOfAddressUpdateOptionsFromFile(
        assignFile,
        kycLive || {},
        sub || {},
        assignGroup?.meta || null
      ),
      ...corporateDataUpdateOptionsFromFile(
        assignFile,
        kycLive || {},
        sub || {},
        assignGroup?.meta || null
      ),
    ],
    [assignFile, assignGroup?.meta, kycLive, sub]
  );

  function fileScanHeaderVisual(file, group) {
    if (isReviewedStatus(file?.review?.status)) return null;

    const poaResult = file?.proofOfAddressCheck || null;
    if (poaResult) {
      const status = safeStr(poaResult?.overallStatus || poaResult?.status);
      return {
        documentIcon: iconUtilityBlueDocHeader,
        documentTitle: "Utility document",
        statusIcon: status === "pending" ? null : status === "passed" ? iconScannOk : iconScannFail,
        statusTitle: proofOfAddressTitle(poaResult),
      };
    }

    const corporateResult = file?.corporateDocumentCheck || null;
    const corporateStatus = safeStr(corporateResult?.overallStatus || corporateResult?.status);
    if (corporateResult && safeStr(corporateResult?.assessmentStatus) === "assessed") {
      const reasons = corporateDocumentReasons(corporateResult);
      const hasCriticalReason = reasons.some((reason) =>
        corporateDocumentReasonBadgeClass(reason, corporateStatus).includes("isCritical")
      );
      const hasOrangeReason = reasons.some((reason) =>
        corporateDocumentReasonBadgeClass(reason, corporateStatus).includes("isManual")
      );
      return {
        documentIcon: iconCorporateDocBlue,
        documentTitle: "Corporate document",
        statusIcon: corporateStatus === "pending" ? null : corporateStatus === "passed" ? iconScannOk : !hasCriticalReason && hasOrangeReason ? iconScannCheck : iconScannFail,
        statusTitle: corporateDocumentTitle(corporateResult),
      };
    }

    const storedResult = file?.documentDataConsistencyCheck || null;
    const result =
      storedResult && storedScanConfirmsIdRelated(storedResult)
        ? compareStoredExtractionWithCurrentKyc(
            storedResult,
            kycLive,
            sub,
            group?.meta || null
          )
        : null;
    if (result && shouldShowDataConsistencyBadge(result)) {
      const status = safeStr(result?.overallStatus || result?.status);
      const isExpired = isDocumentDataExpired(result);
      return {
        documentIcon: iconIdCardBlueDocHeader,
        documentTitle: "Identification document",
        statusIcon: status === "pending" ? null : status === "clear" ? (isExpired ? iconScannCheck : iconScannOk) : iconScannFail,
        statusTitle: dataConsistencyStatusLabel(status),
      };
    }

    const kind = safeStr(group?.meta?.kind);
    if (kind === "sow") {
      return {
        documentIcon: iconSowBlueDoc,
        documentTitle: "Source of wealth corroboration",
      };
    }

    if (kind === "entity") {
      return {
        documentIcon: iconCorporateDocBlue,
        documentTitle: "Corporate document",
      };
    }

    return null;
  }

  function renderFileScanBadges(f, g) {
    return <>
    {(() => {
      if (isReviewedStatus(f?.review?.status)) return null;
      const poaResult = f?.proofOfAddressCheck || null;
      if (!poaResult) return null;
      const status = safeStr(poaResult?.overallStatus || poaResult?.status);
      const reasons = proofOfAddressReasons(poaResult);
      const orderedReasons = sortBadgeItems(reasons, (reason) =>
        proofOfAddressReasonBadgeClass(reason, status)
      );
      const hasCriticalReason = orderedReasons.some((reason) =>
        proofOfAddressReasonBadgeClass(reason, status).includes("isCritical")
      );
      const hasOrangeReason = orderedReasons.some((reason) => {
        const badgeClass = proofOfAddressReasonBadgeClass(reason, status);
        return badgeClass.includes("isExpired") || badgeClass.includes("isManual");
      });
      return (
        <div className="docDataConsistencyInline">
          {status === "passed" ? (
            <>
              <span className="docDataConsistencyResultIcon" title={proofOfAddressTitle(poaResult)}>
                <img src={iconScannOk} alt="" aria-hidden="true" />
              </span>
              <span
                role="button"
                tabIndex={0}
                className="docDataConsistencyClearText"
                title="View details"
                onClick={(event) => {
                  event.stopPropagation();
                  setDocCheckDetail(
                    proofOfAddressDetailForReason(poaResult, {
                      key: "passed",
                      label: "Proof of address data consistent",
                      status: "passed",
                    })
                  );
                }}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  event.preventDefault();
                  event.stopPropagation();
                  setDocCheckDetail(
                    proofOfAddressDetailForReason(poaResult, {
                      key: "passed",
                      label: "Proof of address data consistent",
                      status: "passed",
                    })
                  );
                }}
              >
                Proof of address data consistent
              </span>
            </>
          ) : (
            <>
              {status !== "pending" ? (
                <span className="docDataConsistencyResultIcon" title={proofOfAddressTitle(poaResult)}>
                  <img
                    src={!hasCriticalReason && hasOrangeReason ? iconScannCheck : iconScannFail}
                    alt=""
                    aria-hidden="true"
                    className={
                      !hasCriticalReason && hasOrangeReason
                        ? "docDataConsistencyResultIconImg--check"
                        : "docDataConsistencyResultIconImg--fail"
                    }
                  />
                </span>
              ) : null}
              {orderedReasons.map((reason, reasonIndex) => (
                <span
                  role="button"
                  tabIndex={0}
                  className={`docDataConsistencyInlineBadge ${proofOfAddressReasonBadgeClass(
                    reason,
                    status
                  )}`}
                  key={reason.key || `${reason.label}-${reasonIndex}`}
                  title="View details"
                  onClick={(event) => {
                    event.stopPropagation();
                    setDocCheckDetail(
                      proofOfAddressDetailForReason(poaResult, reason)
                    );
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    event.stopPropagation();
                    setDocCheckDetail(
                      proofOfAddressDetailForReason(poaResult, reason)
                    );
                  }}
                >
                  <span>{reason.label}</span>
                </span>
              ))}
            </>
          )}
        </div>
      );
    })()}

    {(() => {
      if (isReviewedStatus(f?.review?.status)) return null;
      const corporateResult = f?.corporateDocumentCheck || null;
      if (!corporateResult || safeStr(corporateResult?.assessmentStatus) !== "assessed") return null;
      const status = safeStr(corporateResult?.overallStatus || corporateResult?.status);
      const reasons = corporateDocumentReasons(corporateResult);
      if (!reasons.length) return null;
      const orderedReasons = sortBadgeItems(reasons, (reason) =>
        corporateDocumentReasonBadgeClass(reason, status)
      );
      const hasCriticalReason = orderedReasons.some((reason) =>
        corporateDocumentReasonBadgeClass(reason, status).includes("isCritical")
      );
      const hasOrangeReason = orderedReasons.some((reason) => {
        const badgeClass = corporateDocumentReasonBadgeClass(reason, status);
        return badgeClass.includes("isManual");
      });

      return (
        <div className="docDataConsistencyInline">
          {status !== "pending" ? (
            <span className="docDataConsistencyResultIcon" title={corporateDocumentTitle(corporateResult)}>
              <img
                src={status === "passed" ? iconScannOk : !hasCriticalReason && hasOrangeReason ? iconScannCheck : iconScannFail}
                alt=""
                aria-hidden="true"
                className={
                  status !== "passed" && !hasCriticalReason && hasOrangeReason
                    ? "docDataConsistencyResultIconImg--check"
                    : status !== "passed"
                    ? "docDataConsistencyResultIconImg--fail"
                    : undefined
                }
              />
            </span>
          ) : null}
          {orderedReasons.map((reason, reasonIndex) => (
            <span
              role="button"
              tabIndex={0}
              className={`docDataConsistencyInlineBadge ${corporateDocumentReasonBadgeClass(
                reason,
                status
              )}`}
              key={reason.key || `${reason.label}-${reasonIndex}`}
              title="View details"
              onClick={(event) => {
                event.stopPropagation();
                setDocCheckDetail(
                  corporateDocumentDetailForReason(corporateResult, reason)
                );
              }}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                event.stopPropagation();
                setDocCheckDetail(
                  corporateDocumentDetailForReason(corporateResult, reason)
                );
              }}
            >
              <span>{reason.label}</span>
            </span>
          ))}
        </div>
      );
    })()}

    {(() => {
      if (isReviewedStatus(f?.review?.status)) return null;

      const storedResult = f?.documentDataConsistencyCheck || null;
      if (storedResult && !storedScanConfirmsIdRelated(storedResult)) {
        return null;
      }

      const result = compareStoredExtractionWithCurrentKyc(
        storedResult,
        kycLive,
        sub,
        g?.meta || null
      );
      if (!result) {
        return null;
      }

      const title = dataConsistencyTitle(result);
      const showBadge = result && shouldShowDataConsistencyBadge(result);
      const currentKycProfile = currentKycProfileForDocCheck(
        kycLive || {},
        sub || {},
        g?.meta || null
      );
      if (result) {
        logDocCheckCardContent({
          result,
          file: f,
          group: g,
          sub,
          kycProfile: currentKycProfile,
        });
      }
      const status = safeStr(result?.overallStatus || result?.status);
      const shortReason = dataConsistencyShortReason(
        result,
        "ID check pending"
      );
      const shortReasons = dataConsistencyShortReasons(
        result,
        "ID check pending"
      );
      const isExpired = isDocumentDataExpired(result);
      const idReasonBadges = shortReasons.length
        ? shortReasons
        : [
            {
              key: "status",
              label:
                shortReason ||
                dataConsistencyStatusLabel(status),
              status,
            },
          ];
      const orderedIdReasonBadges = sortBadgeItems(idReasonBadges, (reason) =>
        dataConsistencyStatusClass(reason.status || status)
      );
      const hasCriticalIdReason = orderedIdReasonBadges.some((reason) =>
        dataConsistencyStatusClass(reason.status || status).includes("isCritical")
      );
      const hasOrangeIdReason = orderedIdReasonBadges.some((reason) => {
        const badgeClass = dataConsistencyStatusClass(reason.status || status);
        return badgeClass.includes("isExpired") || badgeClass.includes("isManual");
      });

      return (
        <div className="docDataConsistencyInline">
          {status === "clear" ? (
            <>
              <span className="docDataConsistencyResultIcon" title={title}>
                <img
                  src={isExpired ? iconScannCheck : iconScannOk}
                  alt=""
                  aria-hidden="true"
                  className={isExpired ? "docDataConsistencyResultIconImg--check" : undefined}
                />
              </span>
              {isExpired ? (
                <span
                  role="button"
                  tabIndex={0}
                  className="docDataConsistencyInlineBadge isExpired"
                  title="View details"
                  onClick={(event) => {
                    event.stopPropagation();
                    setDocCheckDetail(
                      docCheckDetailForReason(result, {
                        label: "Expired",
                        type: "expiryDate",
                        check: Array.isArray(result?.checks)
                          ? result.checks.find(
                              (check) =>
                                safeStr(check?.type) === "expiryDate"
                            )
                          : null,
                      })
                    );
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    event.stopPropagation();
                    setDocCheckDetail(
                      docCheckDetailForReason(result, {
                        label: "Expired",
                        type: "expiryDate",
                        check: Array.isArray(result?.checks)
                          ? result.checks.find(
                              (check) =>
                                safeStr(check?.type) === "expiryDate"
                            )
                          : null,
                      })
                    );
                  }}
                >
                  Expired
                </span>
              ) : null}
              <span
                role="button"
                tabIndex={0}
                className="docDataConsistencyClearText"
                title="View details"
                onClick={(event) => {
                  event.stopPropagation();
                  setDocCheckDetail(
                    docCheckDetailForReason(result, {
                      key: "clear",
                      label: "ID data consistent",
                      status: "clear",
                    })
                  );
                }}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  event.preventDefault();
                  event.stopPropagation();
                  setDocCheckDetail(
                    docCheckDetailForReason(result, {
                      key: "clear",
                      label: "ID data consistent",
                      status: "clear",
                    })
                  );
                }}
              >
                ID data consistent
              </span>
            </>
          ) : showBadge ? (
            <>
              {status !== "pending" ? (
                <span className="docDataConsistencyResultIcon" title={dataConsistencyStatusLabel(status)}>
                  <img
                    src={!hasCriticalIdReason && hasOrangeIdReason ? iconScannCheck : iconScannFail}
                    alt=""
                    aria-hidden="true"
                    className={
                      !hasCriticalIdReason && hasOrangeIdReason
                        ? "docDataConsistencyResultIconImg--check"
                        : "docDataConsistencyResultIconImg--fail"
                    }
                  />
                </span>
              ) : null}
              {orderedIdReasonBadges.map((reason, reasonIndex) => (
                <span
                  role="button"
                  tabIndex={0}
                  className={`docDataConsistencyInlineBadge ${dataConsistencyStatusClass(
                    reason.status || status
                  )}`}
                  key={reason.key || `${reason.label}-${reasonIndex}`}
                  title="View details"
                  onClick={(event) => {
                    event.stopPropagation();
                    setDocCheckDetail(
                      docCheckDetailForReason(result, reason)
                    );
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    event.stopPropagation();
                    setDocCheckDetail(
                      docCheckDetailForReason(result, reason)
                    );
                  }}
                >
                  <span>{reason.label}</span>
                </span>
              ))}
            </>
          ) : null}
          {isExpired && status !== "clear" ? (
            <span
              role="button"
              tabIndex={0}
              className="docDataConsistencyInlineBadge isExpired"
              title="View details"
              onClick={(event) => {
                event.stopPropagation();
                setDocCheckDetail(
                  docCheckDetailForReason(result, {
                    label: "Expired",
                    type: "expiryDate",
                    check: Array.isArray(result?.checks)
                      ? result.checks.find(
                          (check) =>
                            safeStr(check?.type) === "expiryDate"
                        )
                      : null,
                  })
                );
              }}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                event.stopPropagation();
                setDocCheckDetail(
                  docCheckDetailForReason(result, {
                    label: "Expired",
                    type: "expiryDate",
                    check: Array.isArray(result?.checks)
                      ? result.checks.find(
                          (check) =>
                            safeStr(check?.type) === "expiryDate"
                        )
                      : null,
                  })
                );
              }}
            >
              Expired
            </span>
          ) : null}
        </div>
      );
    })()}
    </>;
  }

  async function confirmKycUpdateYes() {
    if (!hasSelectedKycUpdateOption(activeKycUpdateOptions, assignKycUpdates)) return;
    await approvePendingSelection({ applyKycUpdates: true });
  }

  async function confirmKycUpdateContinue() {
    await approvePendingSelection({ applyKycUpdates: true });
  }

  useEffect(() => {
    if (!kycUpdateConfirmOpen) return undefined;
    function handleEnter(event) {
      if (
        event.key !== "Enter" ||
        event.shiftKey ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        event.isComposing
      ) {
        return;
      }
      event.preventDefault();
      confirmKycUpdateContinue().catch((e) => {
        alert(e?.message || "Approve failed");
      });
    }
    window.addEventListener("keydown", handleEnter);
    return () => window.removeEventListener("keydown", handleEnter);
  }, [kycUpdateConfirmOpen, pendingApproval, assignKycUpdates]);

  useStaffTopBand(
    isModal ? null : {
      title: "INBOX · Documents Review",
      contentClassName: "docTopBandContent",
      smallBandColor: "#8994A0",
      titleColor: "#ffffff",
      right: (
        <div className="d-flex align-items-center gap-2">
          {(sub?.clientName || sub?.clientId || sub?.id) && (
            <span className="docBandClientName">
              {sub?.clientName || sub?.clientId || sub?.id}
            </span>
          )}
          <button
            type="button"
            className="btn btn-sm btn-outline-light docTopBtn"
            onClick={() => onClose ? onClose() : nav(-1)}
            aria-label="Back"
            title="Back"
          >
            <img src={iconReturnWhite} alt="" className="docTopBtnIcon" aria-hidden="true" />
            <img src={iconMailboxD} alt="" className="docTopBtnIcon" aria-hidden="true" />
          </button>
        </div>
      ),
    },
    [isModal, nav, onClose, sub?.clientName, sub?.clientId, sub?.id]
  );

  const activeKycUpdateOptions = pendingApproval?.kycProfileUpdateOptions?.length
    ? pendingApproval.kycProfileUpdateOptions
    : assignKycUpdateOptions;
  const activeKycUpdateIsCorporate = activeKycUpdateOptions.some((option) =>
    safeStr(option?.key).startsWith("corporate")
  );
  const activeKycUpdateIsProofOfAddress = activeKycUpdateOptions.some((option) =>
    safeStr(option?.key).startsWith("poa")
  );

  useEffect(() => {
    if (!kycUpdateConfirmOpen) return undefined;
    const timer = window.setTimeout(() => {
      kycUpdateContinueBtnRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [kycUpdateConfirmOpen]);

  if (loading && !documentOnly) {
    return isModal ? (
      <div className="docModalLoadingState">
        <div className="appDotsLoader" aria-hidden="true" />
      </div>
    ) : null;
  }

  if (err && !documentOnly) {
    return (
      <div className="docTopBandContent docPagePad">
        <div className="alert alert-danger">{err}</div>
        <button
          className="btn btn-outline-secondary docTopBtn"
          onClick={() => onClose ? onClose() : nav(-1)}
          aria-label="Back"
          title="Back"
        >
          <img src={iconReturnWhite} alt="" className="docTopBtnIcon" aria-hidden="true" />
          <img src={iconMailboxD} alt="" className="docTopBtnIcon" aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div className={documentOnly ? "docDirectReview" : `docPage${isModal ? " docPage--modal" : " staffShellContentPage"}`}>
      {!documentOnly && <div className={`docTopBandContent docBottomPad${isModal && (previewOpen || assignOpen || rejectOpen) ? " docContentBlurred" : ""}`}>
        <KycReadOnlyBanner visible={!canDecide}>
          View only for your role — approving, rejecting and review decisions are made by Staff compliance.
        </KycReadOnlyBanner>
        <div className="card shadow-sm docOuterCard">
          <div className="docOuterHeader">
            <div className="docOuterHeaderTitle">
              Requirements & uploaded files
            </div>
            <div className="docOuterHeaderMeta">
              {files.filter(requiresDocumentReview).length
                ? `${files.filter(requiresDocumentReview).length} file(s) • ${groupedByCard.length} requirement card(s)`
                : `No files uploaded • ${groupedByCard.length} requirement card(s)`}
            </div>
          </div>

          <div className="docOuterBody">
            {!groupedByCard.length ? (
              <div className="docEmpty">Nothing to display.</div>
            ) : (
              <div className="docGroups">
                {groupedByCard.map((g) => {
                  const lines = parseRichLines(g.desc);
                  const { displayLines, notes: requirementNotes } =
                    splitRequirementLinesForDisplay(lines);
                  const showLeft = Boolean(g.title || g.desc || g.subtitle);

                  return (
                    <div key={`grp_${g.cardId}`} className="docGroupCard">
                      {/* Group header */}
                      <div className="docGroupHeader">
                        <div className="docGroupHeaderLeft">
                          {(() => {
                            const kind = safeStr(g?.meta?.kind); // "individual" | "entity" | "sow" | ""
                            const typeIconClass =
                              kind === "entity"
                                ? "bi bi-buildings"
                                : kind === "individual"
                                ? "bi bi-person"
                                : "bi bi-piggy-bank";

                            return (
                              <div className="docHdrRow">
                                {/* Rounded-square type icon (entity / individual / sow) */}
                                <div
                                  className="docHdrTypeIcon"
                                  title={kind || "document"}
                                >
                                  <i className={typeIconClass} />
                                </div>

                                {/* Title + subtitle */}
                                <div className="docHdrTextBlock">
                                  <div className="docGroupTitle">
                                    {(() => {
                                      const cleanTitle = stripMetaInTitle(
                                        g.title || "Requirement"
                                      );
                                      const flags = getHeaderFlagsFromMeta(
                                        g.meta
                                      );
                                      const flagSize = "22";

                                      const hasAnyFlag = Boolean(
                                        flags?.incorporation ||
                                          flags?.nationality ||
                                          flags?.domicile
                                      );

                                      // titles without flags get the SoW icon
                                      const showSowIcon = !hasAnyFlag;

                                      return (
                                        <span className="docTitleRow">
                                          {/* Entity: 1 flag */}
                                          {flags.incorporation ? (
                                            <span className="docFlagDesat">
                                              <CircleFlag
                                                countryCode={
                                                  flags.incorporation
                                                }
                                                height={flagSize}
                                              />
                                            </span>
                                          ) : null}

                                          {/* Individual: 2 flags (Nationality + Domicile) */}
                                          {flags.nationality ||
                                          flags.domicile ? (
                                            <span className="docFlagsRow">
                                              {flags.nationality ? (
                                                <span
                                                  className="docFlagDesat"
                                                  title={`Nationality: ${flags.nationality.toUpperCase()}`}
                                                >
                                                  <CircleFlag
                                                    countryCode={
                                                      flags.nationality
                                                    }
                                                    height={flagSize}
                                                  />
                                                </span>
                                              ) : null}

                                              {flags.domicile ? (
                                                <span
                                                  className="docFlagDesat"
                                                  title={`Domicile: ${flags.domicile.toUpperCase()}`}
                                                >
                                                  <CircleFlag
                                                    countryCode={flags.domicile}
                                                    height={flagSize}
                                                  />
                                                </span>
                                              ) : null}
                                            </span>
                                          ) : null}

                                          {/* SoW icon (no flags title - same for all SoW types) */}
                                          {showSowIcon ? (
                                            <span
                                              className="docCircleIcon"
                                              title="Source of wealth"
                                            >
                                              <i className="bi bi-currency-exchange" />
                                            </span>
                                          ) : null}

                                          <span className="docTitleText">
                                            {cleanTitle}
                                          </span>
                                        </span>
                                      );
                                    })()}
                                  </div>

                                  {/* ✅ FIXED label: use section + subtitle, no more "if subtitle then Identification" */}
                                  <div className="docGroupSubtitle">
                                    {sectionLabelFromGroup(g)}
                                    {g.subtitle ? ` • ${g.subtitle}` : ""}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>

                        <span className="badge docGroupBadge">
                          {g.files.length} file(s)
                        </span>
                      </div>

                      <div
                        className={
                          showLeft ? "docGroupGrid hasLeft" : "docGroupGrid"
                        }
                      >
                        {/* Left: requirements */}
                        {showLeft ? (
                          <div className="docReqBox">
                            {lines.length ? (
                              <div className="docReqLines">
                                {displayLines.map((ln, idx) =>
                                  ln.type === "heading" ? (
                                    <div
                                      key={`h_${g.cardId}_${idx}`}
                                      className="docReqHeading"
                                    >
                                      {ln.text}
                                    </div>
                                  ) : (
                                    <div
                                      key={`b_${g.cardId}_${idx}`}
                                      className="docReqBulletRow"
                                    >
                                      <span className="docReqBulletDot">•</span>
                                      <span className="docReqBulletText">
                                        {ln.text}
                                      </span>
                                    </div>
                                  )
                                )}
                                {requirementNotes.map((note, idx) => (
                                  <div
                                    key={`n_${g.cardId}_${idx}`}
                                    className="docReqNoteCard"
                                  >
                                    <span className="docReqNotePrefix">Note:</span>
                                    <span className="docReqNoteBody">
                                      {note.replace(/^Note:\s*/i, "")}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : g.desc ? (
                              <div className="docReqPre">{g.desc}</div>
                            ) : (
                              <div className="docReqEmpty">
                                No requirement text captured.
                              </div>
                            )}
                          </div>
                        ) : null}

                        {/* Right: files list */}
                        <div className="docFilesCol">
                          {!g.files.length ? (
                            <div className="docNoFiles">
                              No files uploaded for this requirement.
                            </div>
                          ) : (
                            g.files.map((f, i) => (
                              <div
                                key={`file_${g.cardId}_${i}`}
                                className={`docFileRow${
                                  f?.downloadURL ? " docFileRow--clickable" : ""
                                }`}
                                role={f?.downloadURL ? "button" : undefined}
                                tabIndex={f?.downloadURL ? 0 : undefined}
                                onClick={() => openPreviewFile(f, g)}
                                onKeyDown={(event) =>
                                  handlePreviewCardKeyDown(event, f, g)
                                }
                              >
                                <div className="docFileMeta">
                                  <div className="docFileHeaderBlock">
                                    {(() => {
                                      const visual = fileScanHeaderVisual(f, g);
                                      if (!visual) return null;
                                      return (
                                        <div className="docFileScanVisual" aria-hidden="true">
                                          <img
                                            src={visual.documentIcon}
                                            alt=""
                                            className="docFileScanVisualDoc"
                                            title={visual.documentTitle}
                                          />
                                        </div>
                                      );
                                    })()}
                                    <div className="docFileTextBlock">
                                      <div className="docFileName">
                                        {f?.originalName || "file"}
                                      </div>

                                      <div className="docFileInfo">
                                        {fileInfoLabel(f)}
                                      </div>
                                    </div>
                                  </div>

                                  {/* ✅ NEW: review status line */}
                                  {f?.review?.status ? (
                                    <div className="docFileReviewRow">
                                      <span
                                        className={`docReviewPill ${
                                          f.review.status === "approved"
                                            ? "isApproved"
                                            : f.review.status === "rejected"
                                            ? "isRejected"
                                            : ""
                                        }`}
                                      >
                                        {f.review.status === "approved"
                                          ? "Approved"
                                          : f.review.status === "rejected"
                                          ? "Rejected"
                                          : f.review.status}
                                      </span>

                                      {f.review.status === "approved" &&
                                      reviewLabelFromFile(f) ? (
                                        <span className="docReviewText">
                                          Approved as:{" "}
                                          <b>{reviewLabelFromFile(f)}</b>
                                        </span>
                                      ) : null}

                                      {f.review.status === "rejected" &&
                                      reviewLabelFromFile(f) ? (
                                        <span className="docReviewText">
                                          Rejected for:{" "}
                                          <b>{reviewLabelFromFile(f)}</b>
                                        </span>
                                      ) : null}

                                      {/* <span className="docReviewMeta">•</span> */}

                                      <span
                                        className="docReviewMeta"
                                        style={{ marginLeft: -5 }}
                                      >
                                        on{" "}
                                        {formatReviewedAt(
                                          f?.review?.reviewedAt
                                        )}
                                        {/* {formatReviewedAt(
                                          `• ${f?.review?.reviewedAt}`
                                        )} */}
                                        {/* {f?.review?.reviewedBy
                                          ? ` • by ${shortUid(
                                              f.review.reviewedBy
                                            )}`
                                          : ""} */}
                                      </span>
                                    </div>
                                  ) : null}

                                  {renderFileScanBadges(f, g)}
                                </div>
                                {/* <div className="docFileMeta">
                                  <div className="docFileName">
                                    {f?.originalName || "file"}
                                  </div>

                                  <div className="docFileInfo">
                                    {fileInfoLabel(f)}
                                  </div>
                                </div> */}

                                <div className="docFileBtns">
                                  <button
                                    className="btn btn-preview docBtnRound"
                                    disabled={!f?.downloadURL}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      openPreviewFile(f, g);
                                    }}
                                  >
                                    Review
                                  </button>

                                  <button
                                    type="button"
                                    className={`btn btn-outline-secondary docBtnRound docTempRerunBtn docIconBtn${
                                      rerunningDocCheckPath === safeStr(f?.objectPath)
                                        ? " isRunning"
                                        : ""
                                    }`}
                                    title="Temporary: rerun document check for this file"
                                    aria-label="Rerun document check"
                                    disabled={
                                      rerunningDocCheckPath === safeStr(f?.objectPath)
                                    }
                                    onPointerDown={(event) => {
                                      event.stopPropagation();
                                    }}
                                    onMouseDown={(event) => {
                                      event.stopPropagation();
                                    }}
                                    onKeyDown={(event) => {
                                      event.stopPropagation();
                                    }}
                                    onClick={async (event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      await rerunDocumentDataConsistencyCheck(f, g);
                                    }}
                                  >
                                    {rerunningDocCheckPath === safeStr(f?.objectPath) ? (
                                      <span className="docTempRerunSpinner" aria-hidden="true" />
                                    ) : (
                                      <i className="bi bi-arrow-clockwise" aria-hidden="true" />
                                    )}
                                  </button>

                                  {/* Download (icon only) */}
                                  <button
                                    className="btn btn-download docBtnRound docIconBtn"
                                    title="Download"
                                    aria-label="Download"
                                    onClick={async (event) => {
                                      event.stopPropagation();
                                      try {
                                        await downloadViaApi({
                                          docId: sub?.id,
                                          file: f,
                                          group: g,
                                          sub,
                                        });
                                      } catch (e) {
                                        alert(e?.message || "Download failed");
                                      }
                                    }}
                                  >
                                    <i className="bi bi-download" />
                                  </button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>}

      {/* --- MODAL PREVIEW ---- */}

      <Modal
        open={previewOpen || directReviewLoading || (documentOnly && !!err)}
        title={directReviewLoading || (documentOnly && err) ? "Document review" : (
          <div className="docPreviewHeaderContent">
            {previewModalTitleForFile({
              file: previewFile,
              group: previewGroup,
              kyc: kycLive,
              sub,
            })}
            <div className="docPreviewHeaderIssues">
              {renderFileScanBadges(files.find(file => file.objectPath === previewFile?.objectPath) || previewFile, previewGroup)}
            </div>
          </div>
        )}
        onClose={() => {
          if (documentOnly) { onClose?.(); return; }
          setPreviewOpen(false);
          setPreviewFile(null);
          setPreviewGroup(null);
          setPreviewImageMode("full");
          setAssignOpen(false);
          setAssignFile(null);
          setAssignGroup(null);
          setAssignOptions([]);
          setAssignSuggestedRequirementKey("");
          setRejectOpen(false);
          setRejectFile(null);
          setRejectGroup(null);
          setRejectOptions([]);
          setRejectSuggestedRequirementKey("");
        }}
        footer={
          previewFile && previewGroup ? (
            <div className="docPreviewFooterStack">
              <div className="docPreviewReviewActions">
                <button
                  className={`btn btn-reject docPreviewActionBtn${rejectOpen ? " isActionSelected" : ""}`}
                  title={
                    !canDecide
                      ? "Read-only for your role"
                      : previewFile?.review?.status === "rejected"
                      ? "Already rejected"
                      : "Reject"
                  }
                  aria-label="Reject"
                  disabled={!canDecide || previewFile?.review?.status === "rejected" || assignOpen || rejectOpen}
                  onClick={() => {
                    setAssignOpen(false);
                    setAssignFile(null);
                    setAssignGroup(null);
                    setAssignOptions([]);
                    setAssignSuggestedRequirementKey("");
                    onRejectClick(previewFile, previewGroup);
                  }}
                >
                  <img src={iconThumbsDown} alt="" className="docPreviewActionIcon" aria-hidden="true" />
                  <span>Reject</span>
                </button>

                <button
                  className={`btn btn-approve docPreviewActionBtn${assignOpen ? " isActionSelected" : ""}`}
                  title={
                    !canDecide
                      ? "Read-only for your role"
                      : previewFile?.review?.status === "approved"
                      ? "Already approved"
                      : "Approve & assign requirement"
                  }
                  aria-label="Approve & assign requirement"
                  disabled={!canDecide || previewFile?.review?.status === "approved" || assignOpen || rejectOpen}
                  onClick={() => onApproveClick(previewFile, previewGroup)}
                >
                  <img src={iconThumbsUp} alt="" className="docPreviewActionIcon" aria-hidden="true" />
                  <span>Approve</span>
                </button>
              </div>

              {rejectPanelReady && rejectFile && rejectGroup ? (
                <motion.div
                  className="docPreviewRequirementPanel"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                >
                  <div className="docPreviewRequirementHint">
                    Select which requirement this rejection corresponds to
                  </div>
                  <div className={`docPreviewRequirementList${rejectSuggestedRequirementKey ? " hasSuggested" : ""}`}>
                    {rejectOptions.map((opt) => {
                      const key = safeStr(opt?.key) || safeStr(opt);
                      const label = safeStr(opt?.label) || safeStr(opt);
                      const id = `preview_rej_${key || label}`;
                      const isSuggested = !!rejectSuggestedRequirementKey && key === rejectSuggestedRequirementKey;
                      const optionIcon = requirementOptionIcon(opt);

                      return (
                        <button
                          type="button"
                          key={id}
                          className={`docAssignRequirementBtn${isSuggested ? " isSuggested" : ""}`}
                          data-requirement-suggested={isSuggested ? "reject" : undefined}
                          onClick={async () => {
                            try {
                              await rejectSingleFile({
                                file: rejectFile,
                                group: rejectGroup,
                                requirementKey: key,
                                requirementText: label,
                              });

                              setRejectOpen(false);
                              setRejectFile(null);
                              setRejectGroup(null);
                              setRejectOptions([]);
                              setRejectSuggestedRequirementKey("");
                              setPreviewOpen(false);
                              setPreviewFile(null);
                              setPreviewGroup(null);
                              setPreviewImageMode("full");
                            } catch (e) {
                              alert(e?.message || "Reject failed");
                            }
                          }}
                        >
                          {optionIcon ? (
                            <img
                              src={optionIcon}
                              alt=""
                              className="docAssignOptionIcon"
                              aria-hidden="true"
                            />
                          ) : null}
                          <span className="docAssignOptionText">{label}</span>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              ) : null}

              {assignPanelReady && assignFile && assignGroup ? (
                <motion.div
                  className="docPreviewRequirementPanel"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                >
                  <div className="docPreviewRequirementHint">
                    Select which requirement this document satisfies
                  </div>
                  <div className={`docPreviewRequirementList${assignSuggestedRequirementKey ? " hasSuggested" : ""}`}>
                    {assignOptions.map((opt) => {
                      const key = safeStr(opt?.key) || safeStr(opt);
                      const label = safeStr(opt?.label) || safeStr(opt);
                      const id = `preview_opt_${key || label}`;
                      const isSuggested = !!assignSuggestedRequirementKey && key === assignSuggestedRequirementKey;
                      const optionIcon = requirementOptionIcon(opt);

                      return (
                        <button
                          type="button"
                          key={id}
                          className={`docAssignRequirementBtn${isSuggested ? " isSuggested" : ""}`}
                          data-requirement-suggested={isSuggested ? "assign" : undefined}
                          onClick={async () => {
                            try {
                              const kycProfileUpdateOptions = kycUpdateOptionsForApprovalSelection(
                                assignFile,
                                assignGroup,
                                key,
                                label
                              );
                              const selectedUpdates = selectedKycUpdatesForOptions(kycProfileUpdateOptions);
                              const approval = {
                                file: assignFile,
                                group: assignGroup,
                                requirementKey: key,
                                requirementText: label,
                                kycProfileUpdateOptions,
                                selectedKycUpdates: selectedUpdates,
                              };
                              setAssignKycUpdates(selectedUpdates);

                              if (kycProfileUpdateOptions.length) {
                                setPendingApproval(approval);
                                setKycUpdateConfirmOpen(true);
                                return;
                              }

                              await approveSingleFile(approval);

                              closeApprovalFlow();
                            } catch (e) {
                              alert(e?.message || "Approve failed");
                            }
                          }}
                        >
                          {optionIcon ? (
                            <img
                              src={optionIcon}
                              alt=""
                              className="docAssignOptionIcon"
                              aria-hidden="true"
                            />
                          ) : null}
                          <span className="docAssignOptionText">{label}</span>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              ) : null}
            </div>
          ) : null
        }
        className={`docModalOverlayPreview staffDocumentPreview ${
          assignOpen || rejectOpen ? "docModalOverlayWithRequirementPanel" : ""
        }`}
        floatingActions={
          previewFile?.downloadURL ? (
            <>
              <a
                className="docModalFloatingActionBtn"
                href={previewFile.downloadURL}
                target="_blank"
                rel="noreferrer"
                title="Open in new tab"
                aria-label="Open in new tab"
              >
                <img src={iconCircleBlueView} alt="" aria-hidden="true" />
              </a>
              <button
                type="button"
                className="docModalFloatingActionBtn"
                title="Download"
                aria-label="Download"
                onClick={async () => {
                  try {
                    await downloadViaApi({
                      docId: sub?.id,
                      file: previewFile,
                      group: previewGroup,
                      sub,
                    });
                  } catch (e) {
                    alert(e?.message || "Download failed");
                  }
                }}
              >
                <img src={iconCircleBlueDownload} alt="" aria-hidden="true" />
              </button>
            </>
          ) : null
        }
        shellHeight={(() => {
          if (!assignOpen && !rejectOpen) return previewModalMetrics.expanded;
          const n = Math.max(
            assignOpen ? assignOptions.length : rejectOptions.length,
            1
          );
          const panel =
            -30 +  // margin-top (negative — overlaps buttons)
            54 +   // padding top (42) + bottom (12)
            50 +   // hint text (safe upper bound, may wrap)
            n * 44 +  // each option button incl. border
            Math.max(0, n - 1) * 8;  // gaps between buttons
          return Math.max(previewModalMetrics.stack - 54 - panel - 8, 200);
        })()}
        embedded={isModal && !documentOnly}
        style={{
          "--doc-preview-stack-height": `${previewModalMetrics.stack}px`,
        }}
      >
        <div className="docPreviewContentWrap">
          <AnimatePresence>
            {(directReviewLoading || (!previewContentLoaded && previewFile?.downloadURL &&
              (isPdfFile(previewFile) || isImageFile(previewFile)))) && (
              <motion.div
                className="docPreviewLoader"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35 }}
              >
                <div className="appDotsLoader" aria-hidden="true" />
              </motion.div>
            )}
          </AnimatePresence>

        {documentOnly && err ? <div className="alert alert-danger">{err}</div> : directReviewLoading ? null : !previewFile?.downloadURL ? (
          <div className="alert alert-warning">No download URL available.</div>
        ) : isPdfFile(previewFile) ? (
          <div className="docPreviewWrap">
            <StaffPdfPreview
              key={previewFile.downloadURL}
              file={previewFile}
              docId={docId}
              onReady={() => setPreviewContentLoaded(true)}
            />
          </div>
        ) : isImageFile(previewFile) ? (
          <div className="docImgPreviewFrame">
            <img
              src={previewFile.downloadURL}
              alt="preview"
              className={`docImgPreview ${
                previewImageMode === "compact" ? "isCompact" : "isFull"
              }`}
              onLoad={(e) => {
                const img = e.currentTarget;
                const parentWidth = img.parentElement?.clientWidth || 0;
                setPreviewImageMode(
                  parentWidth && img.naturalWidth < parentWidth
                    ? "compact"
                    : "full"
                );
                setPreviewContentLoaded(true);
              }}
            />
          </div>
        ) : (
          <div>
            <div className="alert alert-secondary">
              Preview not supported for this file type.
            </div>
          </div>
        )}
        </div>
      </Modal>

      <Modal
        open={!!docCheckDetail}
        title={
          docCheckDetail?.documentKind === "utility"
            ? "UTILITY DOCUMENT"
            : docCheckDetail?.documentKind === "id"
              ? "IDENTIFICATION DOCUMENT"
              : docCheckDetail?.documentKind === "corporate"
                ? "CORPORATE DOCUMENT"
                : docCheckDetail?.documentTitle || docCheckDetail?.title || "Document Details"
        }
        onClose={() => setDocCheckDetail(null)}
        className="docCheckDetailOverlay"
        embedded={isModal && !documentOnly}
        animation="zoom"
        headerIcon={
          docCheckDetail?.documentKind === "utility" ? (
            <img src={iconUtilityBlueDoc} alt="" />
          ) : docCheckDetail?.documentKind === "id" ? (
            <img src={iconIdCardBlueDoc} alt="" />
          ) : docCheckDetail?.documentKind === "corporate" ? (
            <img src={iconCorporateDocBlue} alt="" />
          ) : null
        }
      >
        <div className="docCheckDetailBody">
          {docCheckDetail?.reasonTitle ? (
            <div className={`docCheckReasonCard ${safeStr(docCheckDetail?.status) ? `is-${safeStr(docCheckDetail.status)}` : ""}`}>
              <span className="docCheckReasonEyebrow">{docCheckDetail?.reasonEyebrow || "Issue"}</span>
              <span className="docCheckReasonTitle">{docCheckDetail.reasonTitle}</span>
            </div>
          ) : null}
          {(docCheckDetail?.rows || []).map((row) => {
            const childRows = Array.isArray(row?.rows) ? row.rows : [];
            if (childRows.length) {
              return (
                <div className="docCheckDetailRow is-grouped" key={row.key || childRows.map((child) => `${child.label}_${child.value}`).join("_")}>
                  {childRows.map((child) => (
                    <div className="docCheckDetailSubRow" key={`${child.label}_${child.value}`}>
                      <div className="docCheckDetailLabel">{child.label}</div>
                      <div className="docCheckDetailValue">{child.value}</div>
                    </div>
                  ))}
                </div>
              );
            }
            return (
              <div className="docCheckDetailRow" key={`${row.label}_${row.value}`}>
                <div className="docCheckDetailLabel">{row.label}</div>
                <div className="docCheckDetailValue">{row.value}</div>
              </div>
            );
          })}
          {(docCheckDetail?.sections || []).map((section) => (
            <div
              className={`docCheckDetailSection ${section.variant ? `is-${section.variant}` : ""}`}
              key={section.title}
            >
              <div className="docCheckDetailSectionTitle">{section.title}</div>
              {(section.rows || []).map((row) => (
                <div
                  className={`docCheckDetailRow ${section.variant === "utilityDocumentDetails" || section.variant === "idDocumentDetails" || section.variant === "corporateDocumentDetails" ? "is-utilityDetailCard" : ""}`}
                  key={`${section.title}_${row.label}_${row.value}`}
                >
                  <div className="docCheckDetailLabel">{row.label}</div>
                  <div className="docCheckDetailValue">{row.value}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </Modal>

      <Modal
        open={kycUpdateConfirmOpen}
        title={
          activeKycUpdateIsCorporate
            ? "Update KYC profile from corporate document"
            : activeKycUpdateIsProofOfAddress
            ? "Update KYC profile from utility document"
            : "Update KYC profile from ID"
        }
        onClose={() => {
          setKycUpdateConfirmOpen(false);
          setPendingApproval(null);
        }}
        className="docKycUpdateConfirmOverlay"
        embedded={isModal && !documentOnly}
        animation="zoom"
        headerIcon={<i className="bi bi-person-add" />}
      >
        <div className="docKycUpdateConfirmBody">
          <p className="docKycUpdateConfirmText">
            {activeKycUpdateIsCorporate
              ? "The approved corporate document contains company data that can update the current KYC record."
              : activeKycUpdateIsProofOfAddress
              ? "The approved utility document contains profile data that can update the current KYC record."
              : "The approved ID contains profile data that can update the current KYC record."}
          </p>
          <div className="docKycUpdateOptions">
            {sortKycUpdateOptionsAddFirst(activeKycUpdateOptions).map((option) => (
              <label className="docKycUpdateOption" key={option.key}>
                <input
                  type="checkbox"
                  checked={assignKycUpdates[option.key] !== false}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setAssignKycUpdates((prev) => ({
                      ...prev,
                      [option.key]: checked,
                    }));
                  }}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>

          <div className="docKycUpdateConfirmActions">
            {(() => {
              const hasSelected = hasSelectedKycUpdateOption(activeKycUpdateOptions, assignKycUpdates);
              return (
                <button
                  type="button"
                  className={`docKycUpdateConfirmBtn ${hasSelected ? "isPrimary" : "isSkip"}`}
                  ref={kycUpdateContinueBtnRef}
                  onClick={async () => {
                    try {
                      await confirmKycUpdateContinue();
                    } catch (e) {
                      alert(e?.message || "Approve failed");
                    }
                  }}
                >
                  {hasSelected ? "Update KYC" : "Skip"}
                </button>
              );
            })()}
            <button
              type="button"
              className="docKycUpdateConfirmBtn isPrimary"
              ref={kycUpdateYesBtnRef}
              style={{ display: "none" }}
              tabIndex={-1}
              aria-hidden="true"
              disabled={!hasSelectedKycUpdateOption(activeKycUpdateOptions, assignKycUpdates)}
              onClick={async () => {
                try {
                  await confirmKycUpdateYes();
                } catch (e) {
                  alert(e?.message || "Approve failed");
                }
              }}
            >
              Yes
            </button>
            <button
              type="button"
              className="docKycUpdateConfirmBtn"
              ref={kycUpdateNoBtnRef}
              style={{ display: "none" }}
              tabIndex={-1}
              aria-hidden="true"
              onClick={async () => {
                try {
                  await approvePendingSelection({ applyKycUpdates: false });
                } catch (e) {
                  alert(e?.message || "Approve failed");
                }
              }}
            >
              No
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
